import { useState, useRef, useEffect, useCallback, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocationStore } from '../../stores/locationStore';
import { useBatteryStore } from '../../stores/batteryStore';
import { useAuthStore } from '../../stores/authStore';
import { lazy, Suspense } from 'react';
import { IconRadio, IconSend, IconCopy } from '../../components/icons';
import { DefaultAvatar } from '../../components/DefaultAvatar';
import { useConfigStore } from '../../stores/configStore';
import { useThemeStore } from '../../stores/themeStore';

// Lazy import with stale-chunk recovery. If the user's HTML references a hash that
// no longer exists on the server (because a deploy happened between page load and
// first map open), `import()` rejects with "Failed to fetch dynamically imported module".
// Force a reload so the browser pulls fresh HTML + the current chunk hash.
const RadarMap = lazy(() =>
  import('./components/RadarMap').catch((err) => {
    console.error('[RadarMap] dynamic import failed, reloading:', err);
    try { window.location.reload(); } catch { /* ignore */ }
    // Never resolve — the page is being reloaded, so React won't render a fallback.
    return new Promise<typeof import('./components/RadarMap')>(() => {});
  })
);
import { subscribeMessages, sendMessage, loadCachedMessages, fillMessageGap, cacheGapMessages, type GapFillResult } from '../../services/messageService';

/**
 * Module-scope gap-fill gate. Gap-fill is "once per login / app-start", NOT per
 * RadioPage mount — navigating away from /app and back must not re-run it.
 *
 * Key is `uid:activeSessionId`. activeSessionId is rewritten on each explicit
 * login (writeSession in authStore) so a logout → login cycle naturally produces
 * a new key and re-runs gap-fill. On app relaunch, module state is wiped with the
 * JS engine, so the key is null at first mount and gap-fill runs.
 */
let gapFillCompletedForSessionKey: string | null = null;
import { subscribeDrops } from '../../services/dropService';
import { subscribeRecentUsers } from '../../services/locationService';
import { calcDistance } from '../../lib/distance';
import { coordsToFrequency, frequencyToCoords } from '../../lib/frequency';
import { colors } from '../../styles/colors';
import type { PublicMessage } from '../../types/message';
import type { NearbyUser, LocationDrop } from '../../types/location';

interface ChatMessage {
  id: string;
  text: string;
  senderNickname: string;
  senderId: string;
  gender: 'male' | 'female' | 'unknown';
  profileImageUrl?: string;
  lat: number;
  lng: number;
  frequency: string;
  distance?: number;
  /** Server timestamp in ms (Firestore serverTimestamp) */
  timestamp: number;
  /** Firestore query order index — used for sorting to avoid client clock issues */
  serverOrder: number;
  isBlurred: boolean;
  isMine: boolean;
  isBot: boolean;
  isNearbyNotification: boolean;
}


export default function RadioPage() {
  const { t, i18n } = useTranslation();
  const frequency = useLocationStore((s) => s.frequency);
  const radarRadius = useLocationStore((s) => s.radarRadius);
  const isManualFreq = useLocationStore((s) => s.isManualFreq);
  const cells = useBatteryStore((s) => s.cells);
  const maxCells = useBatteryStore((s) => s.maxCells);
  const user = useAuthStore((s) => s.user);
  const config = useConfigStore((s) => s.config);
  // Track config load state — distance/cost tier calc must use admin-saved tiers,
  // not DEFAULT_CONFIG, otherwise messages received during the early startup window
  // get charged at the default (lower) rate. Subscribe/gap-fill must wait for this.
  const configLoaded = useConfigStore((s) => s.loaded);
  const effectiveLocation = useLocationStore((s) => s.effectiveLocation);
  const messageReceptionRadius = useLocationStore((s) => s.messageReceptionRadius);
  const receptionMode = useLocationStore((s) => s.receptionMode);
  const themeConfig = useThemeStore((s) => s.getThemeConfig());

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [showManualFreq, setShowManualFreq] = useState(false);
  const [manualFreqInput, setManualFreqInput] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [nearbyUsers, setNearbyUsers] = useState<NearbyUser[]>([]);
  const [contextMenu, setContextMenu] = useState<{ msg: ChatMessage; x: number; y: number } | null>(null);
  const [showSensitivityModal, setShowSensitivityModal] = useState(false);
  const [radarUserTab, setRadarUserTab] = useState<'nearby' | 'total'>('nearby');
  const [userListPopup, setUserListPopup] = useState<{ users: NearbyUser[]; title: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const autoScrollRef = useRef(true);
  const showMapRef = useRef(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Expose "not reading" state to AppLayout for unread badge
  useEffect(() => {
    const notReading = !autoScroll || showMap;
    (window as any).__ourhertz_radio_not_reading = notReading;
    // When user returns to reading state, notify AppLayout to clear badge
    if (!notReading) {
      window.dispatchEvent(new CustomEvent('radio-reading'));
    }
    return () => { (window as any).__ourhertz_radio_not_reading = false; };
  }, [autoScroll, showMap]);
  const [scrollDateLabel, setScrollDateLabel] = useState('');
  const scrollDateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDone = useRef(false);
  const programmaticScrollRef = useRef(false);

  // Sync refs
  useEffect(() => { autoScrollRef.current = autoScroll; }, [autoScroll]);
  useEffect(() => { showMapRef.current = showMap; }, [showMap]);

  // Prevent page-level scrolling entirely (RadioPage is a fixed viewport)
  useEffect(() => {
    const isInsideScrollable = (el: Node | null): boolean => {
      let cur = el as HTMLElement | null;
      while (cur && cur !== document.body) {
        if (cur.scrollHeight > cur.clientHeight) {
          const ov = getComputedStyle(cur).overflowY;
          if (ov === 'auto' || ov === 'scroll') return true;
        }
        cur = cur.parentElement;
      }
      return false;
    };
    const prevent = (e: TouchEvent) => {
      // Allow scrolling inside any scrollable container (messages, popups, etc.)
      if (isInsideScrollable(e.target as Node)) return;
      // Block page-level scroll (keyboard drag, form area drag, etc.)
      if (e.cancelable) e.preventDefault();
    };
    document.addEventListener('touchmove', prevent, { passive: false });
    return () => document.removeEventListener('touchmove', prevent);
  }, []);

  // Prevent mobile virtual keyboard from pushing page up + maintain scroll position
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let prevHeight = vv.height;
    const onResize = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;

      const container = messagesContainerRef.current;
      if (!container) { prevHeight = vv.height; return; }

      const heightDiff = vv.height - prevHeight;
      prevHeight = vv.height;

      if (autoScrollRef.current) {
        container.scrollTop = container.scrollHeight;
      } else if (heightDiff !== 0) {
        container.scrollTop -= heightDiff;
      }
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  // Programmatic scroll-to-bottom using scrollTop (not scrollIntoView which can push entire page on mobile)
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    programmaticScrollRef.current = true;
    const container = messagesContainerRef.current;
    if (container) {
      // Always use instant scroll to avoid "scrolling from top" visual glitch
      container.scrollTop = container.scrollHeight;
    }
    setTimeout(() => { programmaticScrollRef.current = false; }, 50);
  }, []);

  const [expandedChat, setExpandedChat] = useState(false);
  const [refreshingFreq, setRefreshingFreq] = useState(false);
  const [drops, setDrops] = useState<LocationDrop[]>([]);
  const syncIntervalSec = useLocationStore((s) => s.syncIntervalSec);
  const [syncCountdown, setSyncCountdown] = useState(syncIntervalSec || 10);
  // Cache processed messages: once a message is processed, its distance/blur/text is fixed forever
  // Key: message ID, Value: ChatMessage (visible) or null (cut/filtered)
  // Persisted to localStorage so blur state survives page refresh
  const processedMsgCacheRef = useRef<Map<string, ChatMessage | null>>(new Map());
  const cacheUidRef = useRef<string | null>(null);

  // Load persisted processed cache from localStorage (keyed per user)
  const loadProcessedCache = useCallback((uid: string) => {
    try {
      const raw = localStorage.getItem(`ourhertz_processed_msgs_${uid}`);
      if (!raw) return;
      const arr: (ChatMessage | null)[] = JSON.parse(raw);
      const map = processedMsgCacheRef.current;
      // Max valid reception radius is 1000km. Entries with distance beyond that could only
      // exist due to a pre-v2.0.442 bug where notifications bypassed the reception cutoff.
      // Filter them at load time so they stop appearing in chat.
      const MAX_VALID_DISTANCE_METERS = 1000 * 1000;
      for (const item of arr) {
        if (item === null) continue; // skip null (cut) entries — they'll be re-evaluated
        if (item.distance !== undefined && item.distance > MAX_VALID_DISTANCE_METERS) continue;
        map.set(item.id, item);
      }
    } catch { /* corrupted data */ }
  }, []);

  // Save processed cache to localStorage (max 300 entries, newest first)
  const saveProcessedCache = useCallback((uid: string) => {
    try {
      const entries = Array.from(processedMsgCacheRef.current.values())
        .filter((v): v is ChatMessage => v !== null)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 300);
      localStorage.setItem(`ourhertz_processed_msgs_${uid}`, JSON.stringify(entries));
    } catch { /* storage full */ }
  }, []);

  // Track which messages we've already charged for
  // Pre-populate with cached message IDs to avoid charging for old messages on reload
  const chargedMsgIds = useRef<Set<string>>(new Set(
    (() => { try { return JSON.parse(localStorage.getItem('ourhertz_msg_cache') || '[]').map((m: any) => m.id); } catch { return []; } })()
  ));

  const pct = maxCells > 0 ? Math.min(100, Math.floor((cells / maxCells) * 100)) : 0;
  const batteryColor = pct > 50 ? colors.batteryHigh : pct > 20 ? colors.batteryMedium : colors.batteryLow;
  const filledBars = Math.ceil((pct / 100) * 4);

  // (Jump mode: one-time cost on activation, no time-based consumption)

  // All users subscription (no time filter - same pool as NearbyPage)
  const activeThreshold = user?.activeThresholdMinutes ?? 60;
  const [allUsersRaw, setAllUsersRaw] = useState<NearbyUser[]>([]);
  useEffect(() => {
    if (!user || !effectiveLocation) return;
    return subscribeRecentUsers(user.uid, effectiveLocation.lat, effectiveLocation.lng, setAllUsersRaw, 100);
  }, [user?.uid, effectiveLocation?.lat, effectiveLocation?.lng]);

  // Radar dots: within radar range AND within activity threshold
  useEffect(() => {
    const radiusMeters = radarRadius * 1000;
    const thresholdMs = activeThreshold * 60 * 1000;
    const now = Date.now();
    const myBlocked = new Set(user?.blockedUserIds ?? []);
    const hideBots = user?.rejectBotMessages === true;
    const filtered = allUsersRaw.filter((u) => {
      if (myBlocked.has(u.uid)) return false;
      if (hideBots && u.isBot) return false;
      if ((u.distance ?? Infinity) > radiusMeters) return false;
      if (u.updatedAt) {
        const ms = u.updatedAt.toMillis ? u.updatedAt.toMillis() : new Date(u.updatedAt as any).getTime();
        if (now - ms > thresholdMs) return false;
      }
      return true;
    });
    // Include myself in nearby count
    if (user && effectiveLocation) {
      const me: NearbyUser = { uid: user.uid, lat: effectiveLocation.lat, lng: effectiveLocation.lng, geohash: '', nickname: user.nickname, gender: user.gender, profileImageUrl: user.profileImageUrl, isOnline: true, updatedAt: null as any, distance: 0 };
      setNearbyUsers([me, ...filtered]);
    } else {
      setNearbyUsers(filtered);
    }
  }, [allUsersRaw, radarRadius, activeThreshold, user?.blockedUserIds, user?.rejectBotMessages]);

  // Helper: get message cost based on distance
  const getMsgCostByDistance = (distanceMeters: number) => {
    const tier = config.batteryConsumption.find((t) => distanceMeters <= t.radius);
    return tier?.cost ?? config.batteryConsumption[config.batteryConsumption.length - 1]?.cost ?? 0;
  };

  // Categorise a preview message for the gap-fill popup checkbox rows.
  type GapCategory = 'notification' | 'charged' | 'blurred' | 'innerFree';
  const categorizeGapMessage = useCallback((m: PublicMessage, myLat: number, myLng: number): { cat: GapCategory; cost: number } => {
    if (m.isNearbyNotification || m.isDropNotification) return { cat: 'notification', cost: 0 };
    const mode = useLocationStore.getState().receptionMode;
    const radarKm = useLocationStore.getState().radarRadius;
    const dist = calcDistance(myLat, myLng, m.lat, m.lng);
    if (mode === 'blur_beyond' && dist > radarKm * 1000) return { cat: 'blurred', cost: 0 };
    const cost = getMsgCostByDistance(dist);
    return cost > 0 ? { cat: 'charged', cost } : { cat: 'innerFree', cost: 0 };
  }, [config.batteryConsumption]);

  // Close map when re-entering radio tab or re-clicking radio tab
  useEffect(() => {
    setShowMap(false); setMapFullscreen(false);
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.tab === '/app') { setShowMap(false); setMapFullscreen(false); setAutoScroll(true); scrollToBottom(); }
    };
    window.addEventListener('tab-reclick', handler);
    return () => window.removeEventListener('tab-reclick', handler);
  }, []);

  // Location sync countdown - pause during manual refresh, auto-fetch at 0
  const syncCountdownRef = useRef(syncIntervalSec || 10);
  useEffect(() => {
    const interval = syncIntervalSec || 10;
    syncCountdownRef.current = interval;
    setSyncCountdown(interval);
    const iv = setInterval(() => {
      if (refreshingFreq) return;
      syncCountdownRef.current -= 1;
      if (syncCountdownRef.current <= 0) {
        // Auto-refresh
        if (!isManualFreq) {
          setRefreshingFreq(true);
          useLocationStore.getState().fetchOnce().finally(() => {
            setTimeout(() => setRefreshingFreq(false), 600);
          });
        }
        syncCountdownRef.current = interval;
      }
      setSyncCountdown(syncCountdownRef.current);
    }, 1000);
    return () => clearInterval(iv);
  }, [syncIntervalSec, refreshingFreq, isManualFreq]);

  // Subscribe to drops for radar display (re-subscribe on auth change)
  useEffect(() => {
    if (!user) return;
    return subscribeDrops(setDrops);
  }, [user?.uid]);

  // Gap fill on mount: preview missed messages and ask user before loading
  // Persists across tab switches / app close via localStorage marker — if user dismisses
  // the popup without choosing Skip or Receive, it will re-appear on next radio tab entry.
  const gapFillDone = useRef(false);
  const [gapFillPreview, setGapFillPreview] = useState<GapFillResult | null>(null);
  const gapFillPreviewRef = useRef<GapFillResult | null>(null);
  useEffect(() => { gapFillPreviewRef.current = gapFillPreview; }, [gapFillPreview]);
  // Subscribe is gated on this — we must not let onSnapshot's normal path process
  // any gap message into cache before fillMessageGap resolves and decides whether a
  // popup is needed. Otherwise Skip ends up "receiving everything" because those
  // messages were silently added by the subscribe fire that won the race.
  const [gapFillHandled, setGapFillHandled] = useState(false);
  // Surface fillMessageGap errors to the user — on real devices the console isn't
  // accessible, so a failed Firestore query would silently produce an empty popup.
  // Banner lets the user report the exact error instead of guessing.
  const [gapFillError, setGapFillError] = useState<string | null>(null);
  // Diagnostic toast for gap-fill outcomes other than "popup shown" — helps the
  // user distinguish "gap-fill ran and truly found 0 messages" from "gap-fill
  // didn't run at all". Auto-dismisses after a few seconds.
  const [gapFillToast, setGapFillToast] = useState<string | null>(null);
  useEffect(() => {
    if (!gapFillToast) return;
    const t = setTimeout(() => setGapFillToast(null), 5000);
    return () => clearTimeout(t);
  }, [gapFillToast]);

  // Reset gap-fill gate on user change so a new session re-gates cleanly. Declared
  // BEFORE the gap-fill effect so it runs first on deps re-evaluation.
  // When this user's session key changes (new login, or user switch), clear the
  // per-mount gating flags so the gap-fill effect picks up the new session and
  // runs exactly once. On tab navigation (same session), this effect also
  // triggers on remount but the gap-fill effect itself checks the module-level
  // gapFillCompletedForSessionKey and will short-circuit to handled=true without
  // showing the processing screen.
  const sessionKey = user ? `${user.uid}:${user.activeSessionId ?? ''}` : null;
  useEffect(() => {
    gapFillDone.current = false;
    // If the session key already matches the one that finished gap-fill, we don't
    // need to go through the processing screen — mark handled immediately.
    setGapFillHandled(sessionKey != null && gapFillCompletedForSessionKey === sessionKey);
  }, [sessionKey]);

  // Long-background resume: on Capacitor native, the JS engine survives a background
  // pause. That means gapFillCompletedForSessionKey persists, and messages that
  // arrived during the pause never pass through the popup flow. When the app returns
  // from a background pause of >= 30 seconds, reset the gate so the next render
  // re-runs fillMessageGap against the now-wider gap.
  const lastPausedAtRef = useRef<number>(0);
  useEffect(() => {
    let removeListener: (() => void) | null = null;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            lastPausedAtRef.current = Date.now();
            return;
          }
          const pausedMs = Date.now() - lastPausedAtRef.current;
          if (lastPausedAtRef.current === 0 || pausedMs < 30_000) return;
          // Long pause — treat as a fresh "session" for gap-fill purposes. Clear the
          // module gate and reset RadioPage state so the effect re-enters.
          gapFillCompletedForSessionKey = null;
          gapFillDone.current = false;
          setGapFillHandled(false);
          setGapFillError(null);
        });
        removeListener = () => handle.remove();
      } catch {
        // @capacitor/app unavailable (web) — no-op.
      }
    })();
    return () => { removeListener?.(); };
  }, []);

  // Load processed-message cache on user change — MUST run before the gap-fill effect
  // so fillMessageGap's alreadyProcessedIds filter has the IDs. Previously this logic
  // lived inside the subscribe effect which runs AFTER gap-fill, so the filter saw an
  // empty set and already-received messages got re-surfaced in the popup.
  // Also shows cached messages immediately so chat history is visible before the live
  // subscription starts (useful during GPS warmup).
  useEffect(() => {
    if (!user) return;
    if (cacheUidRef.current === user.uid) return;
    processedMsgCacheRef.current.clear();
    cacheUidRef.current = user.uid;
    loadProcessedCache(user.uid);

    const cl = Number(localStorage.getItem('ourhertz_chat_cleared_at') || '0');
    const cached = Array.from(processedMsgCacheRef.current.values())
      .filter((m): m is ChatMessage => m !== null && (cl <= 0 || m.timestamp >= cl))
      .sort((a, b) => a.timestamp - b.timestamp);
    if (cached.length > 0) {
      const blocked = new Set(user.blockedUserIds ?? []);
      let f = cached.filter((m) => m.isMine || !blocked.has(m.senderId));
      const gf = user.genderFilter ?? 'all';
      if (gf !== 'all') f = f.filter((m) => m.isMine || m.gender === gf);
      if (user.rejectBotMessages) f = f.filter((m) => m.isMine || !m.isBot);
      setMessages(f);
    }
  }, [user?.uid, loadProcessedCache]);

  // Per-category receive toggles for the 부재 중 메시지 popup.
  // All default to true; user can uncheck categories they don't want to receive.
  // Unselected messages are NOT added to chat and are lost (watermark moves past them).
  const [rxCharged, setRxCharged] = useState(true);
  const [rxBlurred, setRxBlurred] = useState(true);
  const [rxInnerFree, setRxInnerFree] = useState(true);
  const [rxNotification, setRxNotification] = useState(true);

  const PENDING_GAP_KEY = 'ourhertz_pending_gap_fill';

  useEffect(() => {
    if (gapFillDone.current || !user || !effectiveLocation) return;
    // Must wait for admin config to load — otherwise the tier costs passed into
    // fillMessageGap / popup cost calculation come from DEFAULT_CONFIG.
    if (!configLoaded) return;
    // Already completed for this session (probably via a prior RadioPage mount).
    // Skip without showing the processing screen.
    if (sessionKey && gapFillCompletedForSessionKey === sessionKey) {
      gapFillDone.current = true;
      setGapFillHandled(true);
      setGapFillToast((isKo ? '부재중 확인 이미 완료 (세션 캐시)' : 'Missed-check already done this session'));
      return;
    }
    gapFillDone.current = true;

    const curRadarRadius = useLocationStore.getState().radarRadius;
    const curReceptionMode = useLocationStore.getState().receptionMode;
    const curMsgReceptionRadius = useLocationStore.getState().messageReceptionRadius;

    // Check for unresolved pending gap-fill (user closed app / switched tab without choosing)
    let baseline: { lastTs: number; historyHours: number } | null = null;
    try {
      const raw = localStorage.getItem(PENDING_GAP_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved.lastTs === 'number' && saved.lastTs > 0) {
          baseline = {
            lastTs: saved.lastTs,
            historyHours: saved.historyHours ?? (user.messageHistoryHours ?? 6),
          };
        }
      }
    } catch { /* ignore */ }

    // Fresh detection if no pending marker.
    // No time-gap threshold: any missed message on re-entry should trigger the popup.
    // fillMessageGap returns 0 messages if nothing is actually missed — cheap single query.
    // `lastTs === 0` (fresh install / cleared cache) is intentionally fine — fillMessageGap
    // falls back to a Date.now-maxHours window so pre-session messages still flow through
    // the popup/charging path instead of being silently absorbed by the subscribe normal
    // path (which only charges for session-fresh messages).
    if (!baseline) {
      const cached = loadCachedMessages();
      const lastTs = cached.length > 0 ? cached[cached.length - 1].timestampMs : 0;
      // `|| 6` (not `?? 6`) so an explicit 0 or NaN falls back to the default 6h
      // window. Previously a 0 stored on the user doc would silently disable the
      // entire gap-fill / popup flow, which is almost never the intent.
      const historyHours = user.messageHistoryHours || 6;
      baseline = { lastTs, historyHours };
    }

    if (!baseline) {
      // Nothing to gap-fill — let subscribe proceed and mark this session complete
      // so tab navigation doesn't re-enter the processing screen.
      if (sessionKey) gapFillCompletedForSessionKey = sessionKey;
      setGapFillHandled(true);
      return;
    }
    const runBaseline = baseline;

    setGapFilling(true);
    // Snapshot of already-processed message IDs (from prior sessions or this one's
    // live reception) so fillMessageGap doesn't re-surface them in the popup.
    const alreadyProcessed = new Set(processedMsgCacheRef.current.keys());
    const runSessionKey = sessionKey;
    let succeeded = false;
    // Race with a 30s timeout — on a flaky mobile network, getDocs can hang and
    // leave the processing screen up forever. A timeout surfaces the error banner
    // and lets the next navigation retry.
    const timeoutMs = 30_000;
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Timeout after ' + Math.round(timeoutMs / 1000) + 's')), timeoutMs),
    );
    Promise.race([
      fillMessageGap(runBaseline.lastTs, runBaseline.historyHours, effectiveLocation.lat, effectiveLocation.lng, config.batteryConsumption, user.uid, alreadyProcessed, user.lastMessageReceivedAt, curRadarRadius, curReceptionMode, curMsgReceptionRadius, user.rejectBotMessages === true),
      timeoutPromise,
    ])
      .then((result) => {
        succeeded = true;
        if (result.messages.length === 0) {
          // Nothing to receive — clean any stale marker
          try { localStorage.removeItem(PENDING_GAP_KEY); } catch { /* ignore */ }
          setGapFillToast(isKo ? '부재중 메시지 없음' : 'No missed messages');
          return;
        }
        // Persist baseline so popup re-appears if user leaves without choosing
        try {
          localStorage.setItem(PENDING_GAP_KEY, JSON.stringify({
            lastTs: runBaseline.lastTs,
            historyHours: runBaseline.historyHours,
            createdAt: Date.now(),
          }));
        } catch { /* storage full */ }
        // Reset per-category toggles each time the popup is first shown
        setRxCharged(true); setRxBlurred(true); setRxInnerFree(true); setRxNotification(true);
        // Sync ref synchronously so the subsequent subscribe fires (now unblocked)
        // route to the accumulation branch and don't silently add messages to
        // processedMsgCacheRef before the user confirms the popup.
        gapFillPreviewRef.current = result;
        setGapFillPreview(result);
        // The popup itself is the UI signal when N>0 — no additional toast needed.
      })
      .catch((err) => {
        console.error('[gap-fill] fillMessageGap failed:', err);
        const msg = err instanceof Error ? err.message : String(err);
        setGapFillError(msg);
      })
      .finally(() => {
        setGapFilling(false);
        // Only mark the session key as "done" on success so that an error leaves
        // the gate open — the next RadioPage mount will retry fillMessageGap.
        if (succeeded && runSessionKey) gapFillCompletedForSessionKey = runSessionKey;
        // Unblock subscribe — regardless of whether a popup is being shown. If it is,
        // the gapFillPreviewRef guard routes fires to the accumulation branch.
        setGapFillHandled(true);
      });
    // NOTE: gapFillHandled is intentionally in deps so the bg→fg handler can trigger
    // a re-run by flipping it to false (via setGapFillHandled(false)). Without this,
    // clearing the module-scope gate has no observable effect and fillMessageGap never
    // re-fires for messages that arrived during a long background pause.
  }, [user?.uid, effectiveLocation?.lat, configLoaded, sessionKey, gapFillHandled]);

  // Messages subscription with per-message battery charging
  // Distance, blur/cut state, and text are fixed at reception time and never re-evaluated.
  const hasLocation = !!effectiveLocation;
  const [gapFilling, setGapFilling] = useState(false);
  useEffect(() => {
    if (!user) return;
    // Note: processed cache is loaded by the dedicated cache-load effect above,
    // which runs before gap-fill so fillMessageGap can filter already-received IDs.

    if (!hasLocation) return; // Wait for location before subscribing to Firestore
    // Wait for admin config — tier costs must reflect the saved appConfig/main doc,
    // not DEFAULT_CONFIG, or messages processed during startup are charged wrongly.
    if (!configLoaded) return;
    // Wait for gap-fill to finish its decision — if there's a popup coming, we must
    // not let onSnapshot process those messages into cache via the normal path, since
    // that would make Skip effectively equivalent to Receive (messages end up in chat
    // without user consent or cell deduction).
    if (!gapFillHandled) return;

    const unsub = subscribeMessages((rawMsgs, newMsgIds, fromServer) => {
      const currentUser = useAuthStore.getState().user;
      if (!currentUser) return;
      const loc = useLocationStore.getState().effectiveLocation;
      if (!loc) return;

      // Read current settings from store (only applied to NEW messages)
      const curRadius = useLocationStore.getState().radarRadius;
      const curMode = useLocationStore.getState().receptionMode;
      const curMsgRadius = useLocationStore.getState().messageReceptionRadius;
      const maxReceptionMeters = curMode === 'sensitivity_only'
        ? curRadius * 1000
        : curMsgRadius * 1000;

      // While the 부재 중 메시지 popup is pending: freeze chat state and accumulate
      // incoming messages into the preview instead of rendering them.
      if (gapFillPreviewRef.current) {
        const preview = gapFillPreviewRef.current;
        const existingIds = new Set(preview.messages.map(m => m.id));
        let addedCharge = 0;
        let addedCharged = 0;
        let addedBlurred = 0;
        let addedFree = 0;
        let addedNotification = 0;
        const additions: PublicMessage[] = [];

        for (const m of rawMsgs) {
          if (existingIds.has(m.id)) continue;
          if (m.senderId === currentUser.uid) continue; // never include own messages
          if (processedMsgCacheRef.current.has(m.id)) continue; // already processed in earlier session
          if (currentUser.rejectBotMessages && m.isBot) continue; // bots fully hidden

          const dist = calcDistance(loc.lat, loc.lng, m.lat, m.lng);
          const isFreeNotif = m.isNearbyNotification || m.isDropNotification;

          // Cutoff: notifications/drops → radar, regular messages → reception.
          const cutoff = isFreeNotif ? curRadius * 1000 : maxReceptionMeters;
          if (dist > cutoff) continue;

          if (isFreeNotif) {
            additions.push(m); addedFree++; addedNotification++; continue;
          }
          additions.push(m);
          const isBlurred = curMode === 'blur_beyond' && dist > curRadius * 1000;
          if (isBlurred) { addedBlurred++; addedFree++; continue; }
          const cost = getMsgCostByDistance(dist);
          if (cost > 0) { addedCharge += cost; addedCharged++; }
          else { addedFree++; }
        }

        if (additions.length > 0) {
          const allMsgs = [...preview.messages, ...additions];
          const tsList = allMsgs.map(m => m.timestamp?.toMillis?.() ?? 0).filter(t => t > 0);
          const updated: GapFillResult = {
            messages: allMsgs,
            totalCharge: preview.totalCharge + addedCharge,
            freeCount: preview.freeCount + addedFree,
            chargedCount: preview.chargedCount + addedCharged,
            blurredCount: preview.blurredCount + addedBlurred,
            notificationCount: preview.notificationCount + addedNotification,
            oldestTimestamp: preview.oldestTimestamp || (tsList.length ? Math.min(...tsList) : 0),
            newestTimestamp: tsList.length ? Math.max(...tsList) : preview.newestTimestamp,
          };
          gapFillPreviewRef.current = updated;
          setGapFillPreview(updated);
        }
        return; // block normal processing: no setMessages, no charging
      }

      const cache = processedMsgCacheRef.current;
      let pendingCharge = 0;

      // 서버 워터마크 + 채팅 초기화 (새 메시지에만 적용, 캐시된 메시지는 통과)
      const serverWatermark = currentUser.lastMessageReceivedAt ?? 0;
      const chatClearedAt = Number(localStorage.getItem('ourhertz_chat_cleared_at') || '0');
      const msgFloor = Math.max(serverWatermark, chatClearedAt);

      // Step 1: process rawMsgs into the cache only. We do NOT build `result` from
      // rawMsgs — the final display list is always taken from the full cache so
      // cached messages older than the latest-100 Firestore window don't vanish on
      // each onSnapshot fire (was causing chat-flicker regression).
      for (let idx = 0; idx < rawMsgs.length; idx++) {
        const m = rawMsgs[idx];

        // Already cached as a valid ChatMessage — patch timestamp if the resolved
        // serverTimestamp differs (pending → resolved). Own just-sent messages ship
        // with a client Date.now() placeholder and get the real server time here.
        const cachedEntry = cache.get(m.id);
        if (cachedEntry) {
          const resolvedMs = m.timestamp?.toMillis?.() ?? 0;
          if (resolvedMs > 0 && cachedEntry.timestamp !== resolvedMs) {
            cache.set(m.id, { ...cachedEntry, timestamp: resolvedMs });
          }
          continue;
        }
        if (cache.has(m.id)) {
          // Cached as `null` (cut by reception previously) — keep as-is.
          continue;
        }

        const isOwn = m.senderId === currentUser.uid;
        // Pre-session messages from OTHER users (not session-fresh, not cached) MUST
        // go through the gap-fill popup flow — never silently add them here.
        // Own messages are exempt (local Firestore cache fire with pending ts=0).
        if (!isOwn && !newMsgIds.has(m.id)) continue;

        const msgTs = m.timestamp?.toMillis?.() ?? 0;
        if (!isOwn && msgFloor > 0 && msgTs < msgFloor) continue;

        // New message — process with current settings, then cache forever
        const isMine = m.senderId === currentUser.uid;
        let distance: number | undefined;
        let isBlurred = false;

        if (!isMine) {
          distance = calcDistance(loc.lat, loc.lng, m.lat, m.lng);
          const isFreeNotification = m.isNearbyNotification || m.isDropNotification;
          // Notifications follow radar cutoff, regular messages follow reception.
          const cutoffMeters = isFreeNotification ? curRadius * 1000 : maxReceptionMeters;
          if (distance > cutoffMeters) {
            cache.set(m.id, null);
            continue;
          }

          isBlurred = !isFreeNotification && curMode === 'blur_beyond' && distance > curRadius * 1000;

          // Charge only for genuinely new messages — 접속알림/위치드롭은 거리 무관 무료
          if (!chargedMsgIds.current.has(m.id)) {
            chargedMsgIds.current.add(m.id);
            const isHiddenBot = currentUser.rejectBotMessages === true && (m.isBot ?? false);
            if (newMsgIds.has(m.id) && !isBlurred && !isFreeNotification && !isHiddenBot) {
              const cost = getMsgCostByDistance(distance);
              if (cost > 0) pendingCharge += cost;
            }
          }
        }

        const displayText = isBlurred ? m.text.replace(/./g, '\u2588') : m.text;
        // Timestamp: prefer server time; if pending (ts=0 for own just-sent),
        // clamp placeholder to max(Date.now(), lastCachedTs+1) so it sorts at
        // bottom. The patch-on-resolve branch above then corrects it once the
        // real serverTimestamp arrives.
        let finalTs = msgTs;
        if (finalTs === 0) {
          let maxCached = 0;
          for (const v of cache.values()) {
            if (v && v.timestamp > maxCached) maxCached = v.timestamp;
          }
          finalTs = Math.max(Date.now(), maxCached + 1);
        }
        const chatMsg: ChatMessage = {
          id: m.id, text: displayText, senderNickname: m.senderNickname,
          senderId: m.senderId, gender: m.gender, profileImageUrl: m.profileImageUrl,
          lat: m.lat, lng: m.lng, frequency: coordsToFrequency(m.lat, m.lng),
          distance, timestamp: finalTs,
          serverOrder: idx,
          isBlurred, isMine, isBot: m.isBot ?? false,
          isNearbyNotification: m.isNearbyNotification ?? false,
        };
        cache.set(m.id, chatMsg);
      }

      // Step 2: build the display list from the FULL cache, not just rawMsgs.
      // This guarantees that cached messages older than the latest-100 Firestore
      // window stay visible across every onSnapshot fire.
      const result: ChatMessage[] = [];
      for (const entry of cache.values()) {
        if (entry) result.push(entry);
      }

      // Persist processed cache to localStorage (blur state survives refresh)
      if (currentUser.uid) saveProcessedCache(currentUser.uid);

      // Update server-side watermark (throttled: max once per 60s, but immediate on first receipt).
      // IMPORTANT: only advance from session-fresh messages (newMsgIds). Using all rawMsgs would
      // bump the watermark past pre-session messages that were filtered out of display — those
      // messages belong to the gap-fill popup flow, and advancing the watermark past them makes
      // the next session's fillMessageGap query return 0 even when real missed messages exist.
      if (fromServer && newMsgIds.size > 0 && currentUser.uid) {
        let latestTs = 0;
        for (const m of rawMsgs) {
          if (!newMsgIds.has(m.id)) continue;
          const ts = m.timestamp?.toMillis?.() ?? 0;
          if (ts > latestTs) latestTs = ts;
        }
        const prev = currentUser.lastMessageReceivedAt ?? 0;
        if (latestTs > prev && (prev === 0 || latestTs - prev > 60000)) {
          import('firebase/firestore').then(({ doc: fdoc, updateDoc }) => {
            import('../../lib/firebase/app').then(({ db: fdb }) => {
              updateDoc(fdoc(fdb, 'users', currentUser.uid), { lastMessageReceivedAt: latestTs }).catch(() => {});
            });
          });
        }
      }

      // Sort by timestamp (serverOrder is unreliable across sessions/cache)
      result.sort((a, b) => a.timestamp - b.timestamp);

      // Apply user-level filters (block, gender, bot — these CAN change dynamically)
      const blocked = new Set(currentUser.blockedUserIds ?? []);
      let f = result.filter((m) => m.isMine || !blocked.has(m.senderId));
      const gf = currentUser.genderFilter ?? 'all';
      if (gf !== 'all') f = f.filter((m) => m.isMine || m.gender === gf);
      if (currentUser.rejectBotMessages) f = f.filter((m) => m.isMine || !m.isBot);
      setMessages(f);

      if (pendingCharge > 0 && currentUser) {
        useBatteryStore.getState().consume(currentUser.uid, pendingCharge, 'RADIUS_CONSUME');
      }
    });
    return unsub;
  }, [user?.uid, hasLocation, configLoaded, gapFillHandled]);

  // Re-filter cached messages when user-level filters change (block, gender, bot)
  // This does NOT re-process distance/blur — only re-applies display filters
  useEffect(() => {
    if (!user) return;
    const cache = processedMsgCacheRef.current;
    if (cache.size === 0) return;

    const all = Array.from(cache.values()).filter((m): m is ChatMessage => m !== null);
    const blocked = new Set(user.blockedUserIds ?? []);
    let f = all.filter((m) => m.isMine || !blocked.has(m.senderId));
    const gf = user.genderFilter ?? 'all';
    if (gf !== 'all') f = f.filter((m) => m.isMine || m.gender === gf);
    if (user.rejectBotMessages) f = f.filter((m) => m.isMine || !m.isBot);
    f.sort((a, b) => a.timestamp - b.timestamp);
    setMessages(f);
  }, [user?.blockedUserIds, user?.genderFilter, user?.rejectBotMessages]);

  const knownMsgIdsRef = useRef<Set<string>>(new Set());
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (messages.length === 0) return;

    // First load after mount: treat all messages as already seen, scroll to bottom (once)
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      knownMsgIdsRef.current = new Set(messages.map(m => m.id));
      prevMsgCountRef.current = messages.length;
      // Use microtask + rAF to ensure DOM is rendered
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (container) container.scrollTop = container.scrollHeight;
        });
      });
      return;
    }

    // Count genuinely new messages (IDs not seen before, excluding my own)
    let newCount = 0;
    let hasMyNewMsg = false;
    for (const m of messages) {
      if (!knownMsgIdsRef.current.has(m.id)) {
        knownMsgIdsRef.current.add(m.id);
        if (m.isMine) { hasMyNewMsg = true; } else { newCount++; }
      }
    }

    // My own message: always scroll to bottom
    if (hasMyNewMsg) {
      setAutoScroll(true);
      setUnreadCount(0);
      scrollToBottom();
      return;
    }

    // If messages were replaced (e.g. subscription kicked in after cache display)
    // and we're in auto-scroll, just pin to bottom without animation
    if (messages.length !== prevMsgCountRef.current && autoScrollRef.current) {
      prevMsgCountRef.current = messages.length;
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
      if (newCount <= 0) return;
    }
    prevMsgCountRef.current = messages.length;

    if (newCount <= 0) return;
    if (autoScrollRef.current) {
      scrollToBottom();
      setUnreadCount(0);
    } else {
      setUnreadCount((c) => c + newCount);
    }
  }, [messages]);

  useEffect(() => {
    if (!contextMenu) return;
    const close = (e: Event) => {
      // Don't close if tapping inside the context menu itself
      const menu = document.getElementById('ctx-menu');
      if (menu && menu.contains(e.target as Node)) return;
      setContextMenu(null);
    };
    // Use mousedown for desktop, touchend (not touchstart) for mobile
    // Add a small delay so the current tap doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', close);
      document.addEventListener('touchend', close);
    }, 100);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', close); document.removeEventListener('touchend', close); };
  }, [contextMenu]);

  // Send message: normal cost or jumpSendCost when in jump mode
  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || !user || !effectiveLocation || sending || user.chatLocked) return;
    // Content filter check
    const { isContentClean, getFilterMessage } = await import('../../services/contentFilter');
    if (!isContentClean(inputText.trim())) { alert(getFilterMessage(i18n.language)); return; }
    const sendCost = isManualFreq ? (config.manualFreqMode.jumpSendCost ?? 50) : config.messageSendCost;
    if (sendCost > 0 && cells < sendCost) { alert(t('battery.low')); return; }
    setSending(true);
    try {
      await sendMessage({ text: inputText.trim(), senderId: user.uid, senderNickname: user.nickname, gender: user.gender, profileImageUrl: user.profileImageUrl, lat: effectiveLocation.lat, lng: effectiveLocation.lng, radius: radarRadius, manualFreqApplied: isManualFreq, senderEmail: user.email, isGuest: user.isGuest });
      if (sendCost > 0) await useBatteryStore.getState().consume(user.uid, sendCost, 'MESSAGE_SEND');
      setInputText('');
      setAutoScroll(true);
      setUnreadCount(0);
      scrollToBottom();
    } catch (err) { console.error('[Radio] Send failed:', err); }
    finally { setSending(false); }
  };

  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const confirmAndJump = async (freq: string) => {
    // Check cells before jump
    const jumpCost = config.manualFreqMode.consumptionAmount;
    if (useBatteryStore.getState().cells < jumpCost) {
      alert(isKo ? `배터리가 부족합니다. (필요: ${jumpCost.toLocaleString()}셀)` : `Not enough cells. (Need: ${jumpCost.toLocaleString()})`);
      return;
    }
    const jumpRange = user?.locationJumpRange ?? { min: 100, max: 150 };
    const hasRange = jumpRange.max > 0;
    const rangeInfo = hasRange
      ? (isKo
        ? `\n• 점프 범위: 입력 위치로부터 ${jumpRange.min}~${jumpRange.max}m 이내 랜덤 위치`
        : `\n• Jump range: random location within ${jumpRange.min}~${jumpRange.max}m of target`)
      : '';
    const msg = isKo
      ? `위치 점프를 시작하시겠습니까?\n\n• 점프 비용: ${jumpCost.toLocaleString()}셀 (1회)\n• 메시지 발송: ${(config.manualFreqMode.jumpSendCost ?? 50).toLocaleString()}셀/건${rangeInfo}\n• ${isMobile ? '앱 종료' : '브라우저 탭 닫기 또는 종료'} 시 자동 해제`
      : `Start location jump?\n\n• Jump cost: ${jumpCost.toLocaleString()} cells (one-time)\n• Send cost: ${(config.manualFreqMode.jumpSendCost ?? 50).toLocaleString()} cells/msg${rangeInfo}\n• Auto-cancels when ${isMobile ? 'the app closes' : 'the browser tab is closed'}`;
    if (!confirm(msg)) return;
    if (!useLocationStore.getState().teleport(freq, jumpRange)) { alert('Invalid frequency format'); return; }
    // One-time jump cost
    if (jumpCost > 0 && user) await useBatteryStore.getState().consume(user.uid, jumpCost, 'MANUAL_FREQ_CONSUME');
    setShowManualFreq(false);
  };
  const handleTeleport = () => {
    if (!manualFreqInput.trim()) return;
    confirmAndJump(manualFreqInput.trim());
  };
  const handleCopyFreq = () => { navigator.clipboard.writeText(frequency).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };

  const fmtDist = (m?: number) => m == null ? '' : m < 1000 ? `${Math.round(m)}m` : `${(m / 1000).toFixed(1)}km`;
  const fmtTime = (ts: number) => { const d = new Date(ts); return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`; };
  const fmtDate = (ts: number) => { const d = new Date(ts); return `${d.getFullYear()}.${(d.getMonth()+1).toString().padStart(2,'0')}.${d.getDate().toString().padStart(2,'0')}`; };
  const sameDay = (a: number, b: number) => { const d1 = new Date(a), d2 = new Date(b); return d1.getFullYear()===d2.getFullYear() && d1.getMonth()===d2.getMonth() && d1.getDate()===d2.getDate(); };

  const radiusSteps = config.batteryConsumption.map((t) => ({ km: t.radius / 1000, cost: t.cost }));
  const currentRadiusCost = radiusSteps.find((s) => radarRadius <= s.km)?.cost ?? radiusSteps[radiusSteps.length - 1]?.cost ?? 0;
  const gcounts = { male: nearbyUsers.filter((u) => u.gender === 'male').length, female: nearbyUsers.filter((u) => u.gender === 'female').length, unknown: nearbyUsers.filter((u) => u.gender === 'unknown').length };

  // Total active users (within activity threshold, regardless of distance) - includes me
  const totalActiveUsers = (() => {
    const thresholdMs = activeThreshold * 60 * 1000;
    const now = Date.now();
    const myBlocked = new Set(user?.blockedUserIds ?? []);
    const hideBots = user?.rejectBotMessages === true;
    const others = allUsersRaw.filter((u) => {
      // Same exclusions as the 주변 tab (nearbyUsers): blocked users and, when
      // "AI Bot 표시" is off, bots.
      if (myBlocked.has(u.uid)) return false;
      if (hideBots && u.isBot) return false;
      if (!u.updatedAt) return false;
      const ms = u.updatedAt.toMillis ? u.updatedAt.toMillis() : new Date(u.updatedAt as any).getTime();
      return now - ms <= thresholdMs;
    });
    // Add myself to total
    if (user && effectiveLocation) {
      const me: NearbyUser = { uid: user.uid, lat: effectiveLocation.lat, lng: effectiveLocation.lng, geohash: '', nickname: user.nickname, gender: user.gender, profileImageUrl: user.profileImageUrl, isOnline: true, updatedAt: null as any, distance: 0 };
      return [me, ...others];
    }
    return others;
  })();
  const isKo = i18n.language === 'ko';


  const getDotPos = (u: NearbyUser) => {
    if (!effectiveLocation) return null;
    const rm = radarRadius * 1000, dist = u.distance ?? 0;
    if (dist > rm) return null;
    const lat1 = effectiveLocation.lat * Math.PI / 180, lat2 = u.lat * Math.PI / 180;
    const dLng = (u.lng - effectiveLocation.lng) * Math.PI / 180;
    const bearing = Math.atan2(Math.sin(dLng) * Math.cos(lat2), Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng));
    const rawScale = Math.min(dist / rm, 1) * 42;
    const scale = dist > 0 ? Math.max(rawScale, 8) : 0;
    return { cx: 50 + scale * Math.sin(bearing), cy: 50 - scale * Math.cos(bearing) };
  };

  // Display sort is by timestamp — serverOrder is only set on the subscribe path
  // (0..99 from rawMsgs idx) and is 0 for gap-fill Receive entries, which would put
  // them at the top of the chat instead of their chronological position.
  const sorted = [...messages].sort((a, b) => a.timestamp - b.timestamp);
  const genderColor = (g: string) => g === 'male' ? '#4a9eff' : g === 'female' ? '#ff6b9d' : '#999';

  // Reception range options (km)
  const receptionRangeOptions = [5, 10, 50, 100, 200, 500, 1000];

  // Location permission check
  const permissionState = useLocationStore((s) => s.permissionState);
  const permissionGranted = useLocationStore((s) => s.permissionGranted);
  const hadLocationEver = useRef(false);
  if (effectiveLocation) hadLocationEver.current = true;
  const [locationTimeout, setLocationTimeout] = useState(false);
  useEffect(() => {
    // Only start timeout after permission state is known
    if (permissionState === 'unknown') return;
    if (hadLocationEver.current || permissionGranted) return;
    const timer = setTimeout(() => { if (!hadLocationEver.current) setLocationTimeout(true); }, 10000);
    return () => clearTimeout(timer);
  }, [permissionState, permissionGranted]);

  // Show permission screen only if explicitly denied.
  // Do NOT show when permission is 'granted' but GPS fix is pending (cold start, indoors).
  const showPermissionScreen = !hadLocationEver.current && !effectiveLocation && (
    permissionState === 'denied' ||
    (locationTimeout && permissionState !== 'unknown' && permissionState !== 'granted')
  );

  // GPS fix pending + gap-fill processing: we used to render a full-screen overlay that
  // completely hid the chat, but that caused the chat to flicker on every navigation /
  // refresh and prevented users from reading their cached history while location warmed
  // up. Now both are surfaced as small top banners inside the chat; the cached messages
  // stay visible the whole time and any popup-worthy missed messages still pop up via
  // the existing gapFillPreview modal overlay.
  const showGpsWaiting = !hadLocationEver.current && !effectiveLocation
    && permissionState === 'granted' && !showPermissionScreen;
  const showGapFillProcessing = !!effectiveLocation && !gapFillHandled;

  if (showPermissionScreen) {
    const isDenied = permissionState === 'denied';
    const isNativeDevice = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.isNativePlatform?.();
    const isIOS = isNativeDevice && (window as any).Capacitor?.getPlatform?.() === 'ios';
    const isAndroid = isNativeDevice && (window as any).Capacitor?.getPlatform?.() === 'android';

    const openAppSettings = async () => {
      try {
        const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
        if (isIOS) {
          await NativeSettings.openIOS({ option: IOSSettings.App });
        } else {
          await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
        }
      } catch {
        // Fallback: just reload
        window.location.reload();
      }
    };

    let deniedMessage: string;
    if (isDenied) {
      if (isIOS) {
        deniedMessage = isKo
          ? '위치 권한이 차단되어 있습니다.\n\n설정 → OurHertz(우리들의 주파수) → 위치에서\n"앱을 사용하는 동안" 또는 "항상"을 선택해주세요.'
          : 'Location permission is blocked.\n\nGo to Settings → OurHertz → Location,\nthen select "While Using the App" or "Always".';
      } else if (isAndroid) {
        deniedMessage = isKo
          ? '위치 권한이 차단되어 있습니다.\n\n설정 → 앱 → OurHertz → 권한 → 위치에서\n"앱 사용 중에만 허용"을 선택해주세요.'
          : 'Location permission is blocked.\n\nGo to Settings → Apps → OurHertz → Permissions → Location,\nthen select "Allow only while using the app".';
      } else {
        deniedMessage = isKo
          ? '위치 권한이 차단되어 있습니다.\n브라우저 주소창 왼쪽의 자물쇠/설정 아이콘을 클릭하여\n위치 권한을 허용으로 변경한 후 새로고침 해주세요.'
          : 'Location permission is blocked.\nClick the lock/settings icon in the address bar\nto allow location access, then refresh.';
      }
    } else {
      deniedMessage = isKo
        ? '이 서비스는 GPS 위치 정보를 기반으로 작동합니다.\n위치 권한을 허용해주세요.'
        : 'This service requires GPS location.\nPlease grant location permission.';
    }

    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 overflow-hidden" style={{ backgroundColor: colors.background }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.error} strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
        <h2 className="text-lg font-bold" style={{ color: colors.text }}>{isKo ? '위치 권한이 필요합니다' : 'Location Permission Required'}</h2>
        <p className="text-center text-sm whitespace-pre-line" style={{ color: colors.textMuted }}>
          {deniedMessage}
        </p>
        {isDenied ? (
          <div className="flex gap-3">
            {isNativeDevice && (
              <button onClick={openAppSettings}
                className="rounded-lg px-6 py-3 text-sm font-bold text-white" style={{ backgroundColor: colors.primary }}>
                {isKo ? '설정 열기' : 'Open Settings'}
              </button>
            )}
            <button onClick={() => window.location.reload()}
              className="rounded-lg px-6 py-3 text-sm font-bold"
              style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}>
              {isKo ? '새로고침' : 'Refresh'}
            </button>
          </div>
        ) : (
          <button onClick={async () => {
            const granted = await useLocationStore.getState().requestPermission();
            if (granted) setLocationTimeout(false);
          }}
            className="rounded-lg px-6 py-3 text-sm font-bold text-white" style={{ backgroundColor: colors.primary }}>
            {isKo ? '위치 권한 허용' : 'Allow Location'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden relative" style={{ backgroundColor: colors.background }}>

      {/* GPS still warming up on cold start — subtle banner, chat stays visible. */}
      {showGpsWaiting && (
        <div className="shrink-0 px-3 py-1.5 text-center text-xs"
          style={{ backgroundColor: colors.card, color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
          <span className="inline-block mr-1.5 animate-pulse" style={{ color: colors.primary }}>●</span>
          {isKo ? 'GPS 신호 수신 중…' : 'Acquiring GPS signal…'}
        </div>
      )}

      {/* Gap-fill in flight — small banner instead of a full-screen cover so the
          cached chat stays visible. When fillMessageGap returns messages, the
          gapFillPreview popup overlays the chat as usual. */}
      {showGapFillProcessing && !showGpsWaiting && (
        <div className="shrink-0 px-3 py-1.5 text-center text-xs"
          style={{ backgroundColor: colors.card, color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
          <span className="inline-block mr-1.5 animate-pulse" style={{ color: colors.primary }}>●</span>
          {isKo ? '부재 중 메시지 확인 중…' : 'Checking missed messages…'}
        </div>
      )}

      {/* Gap-fill diagnostic toast — auto-dismisses. Helps the user see that
          fillMessageGap actually ran and returned 0 (vs didn't run at all). */}
      {gapFillToast && !gapFillError && !gapFillPreview && (
        <div className="shrink-0 px-3 py-1.5 text-center text-xs"
          style={{ backgroundColor: colors.card, color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
          <span className="inline-block mr-1.5" style={{ color: colors.primary }}>ℹ</span>
          {gapFillToast}
        </div>
      )}

      {/* Gap-fill error banner — appears if fillMessageGap threw, so the user
          sees the problem instead of the popup silently not showing. */}
      {gapFillError && (
        <div className="shrink-0 px-3 py-1.5 text-center text-xs flex items-center justify-between gap-2"
          style={{ backgroundColor: colors.error, color: '#fff' }}>
          <span className="flex-1 truncate">
            {isKo ? '부재중 메시지 조회 실패: ' : 'Missed-message query failed: '}{gapFillError}
          </span>
          <button
            onClick={() => setGapFillError(null)}
            className="shrink-0 rounded px-2 py-0.5 text-[0.625rem] font-bold"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
            {isKo ? '닫기' : 'Close'}
          </button>
        </div>
      )}

      {/* Gap fill confirmation modal */}
      {gapFillPreview && (() => {
        const gapMs = gapFillPreview.newestTimestamp - gapFillPreview.oldestTimestamp;
        const agoMs = Date.now() - gapFillPreview.oldestTimestamp;
        const formatDuration = (ms: number) => {
          const h = Math.floor(ms / 3600000);
          const m = Math.floor((ms % 3600000) / 60000);
          if (h > 0 && m > 0) return isKo ? `${h}시간 ${m}분` : `${h}h ${m}m`;
          if (h > 0) return isKo ? `${h}시간` : `${h}h`;
          return isKo ? `${m}분` : `${m}m`;
        };
        const currentCells = useBatteryStore.getState().cells;
        const innerFreeCount = Math.max(0, gapFillPreview.freeCount - gapFillPreview.blurredCount - gapFillPreview.notificationCount);
        const selectedCost = rxCharged ? gapFillPreview.totalCharge : 0;
        const selectedCount =
          (rxCharged ? gapFillPreview.chargedCount : 0)
          + (rxBlurred ? gapFillPreview.blurredCount : 0)
          + (rxInnerFree ? innerFreeCount : 0)
          + (rxNotification ? gapFillPreview.notificationCount : 0);
        const notEnough = selectedCost > 0 && currentCells < selectedCost;
        const nothingSelected = selectedCount === 0;
        const receiveDisabled = notEnough || nothingSelected;

        const Row = (props: { checked: boolean; onToggle: () => void; disabled?: boolean; color: string; label: string; }) => (
          <label
            className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm ${props.disabled ? 'opacity-40' : 'cursor-pointer'}`}
            style={{ color: props.color, backgroundColor: 'transparent' }}>
            <input type="checkbox"
              checked={props.checked}
              disabled={props.disabled}
              onChange={props.onToggle}
              className="h-4 w-4 shrink-0 cursor-pointer accent-current"
            />
            <span className="flex-1">{props.label}</span>
          </label>
        );

        return (
          <div className="absolute inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
            <div className="w-full max-w-sm rounded-xl p-5" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
              <h3 className="text-base font-bold mb-3" style={{ color: colors.text }}>
                {isKo ? '부재 중 메시지' : 'Missed Messages'}
              </h3>

              <p className="text-xs mb-2" style={{ color: colors.textMuted }}>
                {isKo
                  ? `약 ${formatDuration(agoMs)} 전 ~ ${formatDuration(Date.now() - gapFillPreview.newestTimestamp)} 전 (${formatDuration(gapMs)} 동안)`
                  : `${formatDuration(agoMs)} ago ~ ${formatDuration(Date.now() - gapFillPreview.newestTimestamp)} ago (${formatDuration(gapMs)} span)`}
              </p>

              <p className="text-sm mb-2" style={{ color: colors.text }}>
                {isKo
                  ? `총 ${gapFillPreview.messages.length}건 중 수신할 항목을 선택하세요`
                  : `Select categories to receive (${gapFillPreview.messages.length} total)`}
              </p>

              <div className="space-y-0.5 mb-3 rounded-md p-1" style={{ backgroundColor: colors.surface }}>
                <Row
                  checked={rxCharged}
                  disabled={gapFillPreview.chargedCount === 0}
                  onToggle={() => setRxCharged((v) => !v)}
                  color={colors.primary}
                  label={isKo
                    ? `레이더 범위 내: ${gapFillPreview.chargedCount}건 → ${gapFillPreview.totalCharge.toLocaleString()}셀`
                    : `Within radar: ${gapFillPreview.chargedCount} → ${gapFillPreview.totalCharge.toLocaleString()} cells`}
                />
                <Row
                  checked={rxBlurred}
                  disabled={gapFillPreview.blurredCount === 0}
                  onToggle={() => setRxBlurred((v) => !v)}
                  color={colors.textMuted}
                  label={isKo
                    ? `레이더 밖 (블러): ${gapFillPreview.blurredCount}건 (무료)`
                    : `Beyond radar (blurred): ${gapFillPreview.blurredCount} (free)`}
                />
                <Row
                  checked={rxInnerFree}
                  disabled={innerFreeCount === 0}
                  onToggle={() => setRxInnerFree((v) => !v)}
                  color={colors.textMuted}
                  label={isKo
                    ? `1km 이내: ${innerFreeCount}건 (무료)`
                    : `Within 1km: ${innerFreeCount} (free)`}
                />
                <Row
                  checked={rxNotification}
                  disabled={gapFillPreview.notificationCount === 0}
                  onToggle={() => setRxNotification((v) => !v)}
                  color={colors.textMuted}
                  label={isKo
                    ? `접속알림/위치드롭: ${gapFillPreview.notificationCount}건 (무료)`
                    : `Notifications/drops: ${gapFillPreview.notificationCount} (free)`}
                />
              </div>

              <div className="text-xs mb-3 flex justify-between" style={{ color: colors.textMuted }}>
                <span>{isKo ? '보유 셀' : 'Your cells'}: {currentCells.toLocaleString()}</span>
                {selectedCost > 0 && (
                  <span style={{ color: notEnough ? colors.error : colors.text }}>
                    {isKo ? '필요 셀' : 'Cost'}: {selectedCost.toLocaleString()}
                  </span>
                )}
              </div>

              <div className="text-xs mb-4 rounded p-2.5" style={{ backgroundColor: colors.surface, color: '#ffaa00' }}>
                {isKo
                  ? '선택하지 않은 메시지는 이후에 받을 수 없습니다.'
                  : 'Unchecked messages cannot be retrieved later.'}
              </div>

              <div className="flex gap-2">
                <button onClick={() => {
                  // +1 so the boundary (msgTs === newestTimestamp) is excluded by the
                  // subscribe filter `msgTs < msgFloor` — without this, skipped messages
                  // can still slip through the live path into processed cache and reappear
                  // in the next session's popup/chat.
                  // IMPORTANT: use the server timestamp from the preview, never Date.now().
                  // If newestTimestamp is 0 (rare — pending serverTimestamp), skip the
                  // watermark update. A client-clock fallback could advance the watermark
                  // past legitimate future messages if the device clock is skewed.
                  const newestServerTs = gapFillPreview.newestTimestamp;
                  gapFillPreviewRef.current = null;
                  setGapFillPreview(null);
                  try { localStorage.removeItem(PENDING_GAP_KEY); } catch { /* ignore */ }
                  if (user && newestServerTs > 0) {
                    const latestTs = newestServerTs + 1;
                    // Optimistic local update so subscribe's msgFloor filter sees the
                    // new watermark immediately (Firestore round-trip is ~100-500ms).
                    const authState = useAuthStore.getState();
                    if (authState.user) {
                      useAuthStore.setState({
                        user: { ...authState.user, lastMessageReceivedAt: latestTs },
                      });
                    }
                    import('firebase/firestore').then(({ doc: fdoc, updateDoc }) => {
                      import('../../lib/firebase/app').then(({ db: fdb }) => {
                        updateDoc(fdoc(fdb, 'users', user.uid), { lastMessageReceivedAt: latestTs }).catch(() => {});
                      });
                    });
                  }
                }}
                  className="flex-1 rounded-lg px-4 py-2.5 text-sm font-bold"
                  style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}>
                  {isKo ? '건너뛰기' : 'Skip'}
                </button>
                <button onClick={() => {
                  const preview = gapFillPreview;
                  if (receiveDisabled) return;
                  if (notEnough) {
                    alert(isKo
                      ? `셀이 부족합니다.\n필요: ${selectedCost.toLocaleString()}셀 / 보유: ${currentCells.toLocaleString()}셀`
                      : `Not enough cells.\nNeeded: ${selectedCost.toLocaleString()} / Have: ${currentCells.toLocaleString()}`);
                    return;
                  }

                  // Process each preview message through the distance/blur pipeline so
                  // they appear in the chat immediately (sorted chronologically). Skip
                  // any category the user unchecked — those messages are dropped and
                  // the watermark will move past them so they can't be re-requested.
                  const loc = useLocationStore.getState().effectiveLocation;
                  const currentUser = useAuthStore.getState().user;
                  if (!loc || !currentUser) return;

                  const cache = processedMsgCacheRef.current;
                  let chargedCellsToDeduct = 0;
                  for (const m of preview.messages) {
                    // Preserve reception-time state: if this id is already a valid
                    // ChatMessage in cache (processed at some earlier moment), keep it as-is.
                    // A `null` entry means it was cut by reception at some point — reception
                    // settings may have widened since, so we allow reprocessing.
                    if (cache.get(m.id)) continue;
                    const isMine = m.senderId === currentUser.uid;
                    if (isMine) continue; // my own messages should not appear here
                    const { cat, cost } = categorizeGapMessage(m, loc.lat, loc.lng);
                    const include =
                      (cat === 'notification' && rxNotification)
                      || (cat === 'blurred' && rxBlurred)
                      || (cat === 'innerFree' && rxInnerFree)
                      || (cat === 'charged' && rxCharged);
                    // Mark as seen regardless of include, so live subscribe never double-processes
                    chargedMsgIds.current.add(m.id);
                    if (!include) continue;

                    const isBlurred = cat === 'blurred';
                    const distance = calcDistance(loc.lat, loc.lng, m.lat, m.lng);
                    const displayText = isBlurred ? m.text.replace(/./g, '\u2588') : m.text;
                    // Gap-fill messages are always server-confirmed so toMillis() must
                    // be a real server time. If somehow missing, skip the entry rather
                    // than silently substituting Date.now() which would misplace it.
                    const msgTs = m.timestamp?.toMillis?.() ?? 0;
                    if (msgTs === 0) continue;
                    const chatMsg: ChatMessage = {
                      id: m.id, text: displayText, senderNickname: m.senderNickname,
                      senderId: m.senderId, gender: m.gender, profileImageUrl: m.profileImageUrl,
                      lat: m.lat, lng: m.lng, frequency: coordsToFrequency(m.lat, m.lng),
                      distance, timestamp: msgTs,
                      serverOrder: 0,
                      isBlurred, isMine: false, isBot: m.isBot ?? false,
                      isNearbyNotification: m.isNearbyNotification ?? false,
                    };
                    cache.set(m.id, chatMsg);
                    if (cat === 'charged') chargedCellsToDeduct += cost;
                  }
                  // Persist cache so reloads don't lose them
                  saveProcessedCache(currentUser.uid);
                  // Append ALL preview messages (even unchecked) to raw cache so subscribeMessages
                  // doesn't treat them as new again.
                  cacheGapMessages(preview.messages);

                  // Advance server watermark past the newest preview message so any
                  // unchecked (skipped) messages won't come back via other devices.
                  // +1 so the boundary message (msgTs === newestTimestamp) is excluded
                  // by the subscribe filter (`msgTs < msgFloor`).
                  // IMPORTANT: derive from server timestamp only — Date.now() fallback
                  // could advance the watermark past legitimate messages if the device
                  // clock is skewed.
                  const newestServerTs = preview.newestTimestamp;
                  if (newestServerTs > 0) {
                    const latestTs = newestServerTs + 1;
                    // Optimistic local authStore update so in-flight onSnapshot
                    // normal-path processing sees the new watermark right away.
                    const authState = useAuthStore.getState();
                    if (authState.user) {
                      useAuthStore.setState({
                        user: { ...authState.user, lastMessageReceivedAt: latestTs },
                      });
                    }
                    import('firebase/firestore').then(({ doc: fdoc, updateDoc }) => {
                      import('../../lib/firebase/app').then(({ db: fdb }) => {
                        updateDoc(fdoc(fdb, 'users', currentUser.uid), { lastMessageReceivedAt: latestTs }).catch(() => {});
                      });
                    });
                  }

                  // Rebuild messages state from processed cache with current user filters
                  const cl = Number(localStorage.getItem('ourhertz_chat_cleared_at') || '0');
                  const all = Array.from(cache.values())
                    .filter((v): v is ChatMessage => v !== null && (cl <= 0 || v.timestamp >= cl));
                  const blocked = new Set(currentUser.blockedUserIds ?? []);
                  let f = all.filter((m) => m.isMine || !blocked.has(m.senderId));
                  const gf = currentUser.genderFilter ?? 'all';
                  if (gf !== 'all') f = f.filter((m) => m.isMine || m.gender === gf);
                  if (currentUser.rejectBotMessages) f = f.filter((m) => m.isMine || !m.isBot);
                  f.sort((a, b) => a.timestamp - b.timestamp);
                  setMessages(f);

                  // Consume cells for selected charged messages only
                  if (chargedCellsToDeduct > 0) {
                    useBatteryStore.getState().consume(currentUser.uid, chargedCellsToDeduct, 'RADIUS_CONSUME');
                  }

                  // Clear pending marker and close popup
                  gapFillPreviewRef.current = null;
                  setGapFillPreview(null);
                  try { localStorage.removeItem(PENDING_GAP_KEY); } catch { /* ignore */ }
                  // Ensure chat scrolls to the newly-added bottom on next render
                  setAutoScroll(true);
                  setUnreadCount(0);
                }}
                  disabled={receiveDisabled}
                  className="flex-1 rounded-lg px-4 py-2.5 text-sm font-bold text-white"
                  style={{ backgroundColor: receiveDisabled ? colors.textMuted : colors.primary, opacity: receiveDisabled ? 0.5 : 1 }}>
                  {isKo
                    ? (nothingSelected ? '수신' : `수신${selectedCost > 0 ? ` (${selectedCost.toLocaleString()}셀)` : ' (무료)'}`)
                    : (nothingSelected ? 'Load' : `Load${selectedCost > 0 ? ` (${selectedCost.toLocaleString()} cells)` : ' (free)'}`)}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="relative m-1.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl p-2.5"
        style={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}` }}>

        {/* Frequency LCD - single row: FREQ label + frequency + buttons */}
        <div className={`mb-1 flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 ${expandedChat ? 'hidden' : ''}`} style={{ backgroundColor: colors.lcdBg, border: `2px solid ${colors.lcdBorder}`, boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5)' }}>
          <span className="shrink-0 text-[0.5625rem] font-bold uppercase tracking-wider" style={{ color: colors.lcdBorder }}>{isKo ? '주파수' : 'FREQ'}</span>
          <p className="font-lcd lcd-glow min-w-0 flex-1 text-center font-bold transition-opacity duration-300" style={{ color: colors.lcdText, fontSize: 'clamp(0.6875rem, 3.2vw, 1rem)', letterSpacing: '0.06em', whiteSpace: 'nowrap', opacity: refreshingFreq ? 0.3 : 1 }}>{frequency}</p>
          <div className="flex shrink-0 items-center gap-1">
            {!isManualFreq && (<>
            <button onClick={async () => {
              setRefreshingFreq(true);
              setSyncCountdown(syncIntervalSec || 10);
              await useLocationStore.getState().fetchOnce();
              setTimeout(() => setRefreshingFreq(false), 600);
            }} className={`rounded p-0.5 hover:bg-white/10 ${refreshingFreq ? 'animate-spin' : ''}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={refreshingFreq ? colors.lcdNeon : colors.lcdBorder} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            </button>
            <span className="font-lcd w-[1.25rem] text-center text-[0.5625rem] font-bold tabular-nums" style={{ color: colors.lcdNeon + 'aa' }}>{syncCountdown}</span>
            </>)}
            <button onClick={handleCopyFreq} className="rounded p-0.5 hover:bg-white/10"><IconCopy size={12} color={copied ? colors.lcdNeon : colors.lcdBorder} /></button>
            <button onClick={() => {
                if (!showManualFreq && isManualFreq) {
                  // Restore current jump frequency when reopening during jump
                  const jumpFreq = useLocationStore.getState().manualFrequency ?? '';
                  setManualFreqInput(jumpFreq);
                }
                setShowManualFreq(!showManualFreq);
              }}
              className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[0.5625rem] font-bold"
              style={{ backgroundColor: isManualFreq ? 'rgba(255,179,0,0.15)' : 'transparent', color: isManualFreq ? '#FFB300' : colors.lcdText, border: `1px solid ${isManualFreq ? '#FFB300' : colors.lcdBorder}` }}>
              ⚡ {isManualFreq ? 'ON' : (isKo ? '위치 점프' : 'Jump')}
            </button>
          </div>
        </div>

        {/* Jump input - inline layout */}
        {showManualFreq && !expandedChat && (() => {
          const raw = manualFreqInput.replace(/-/g, '').toUpperCase();
          const isComplete = raw.length === 12;
          const validCoords = isComplete ? frequencyToCoords(manualFreqInput.trim() || raw.replace(/(.{3})/g, '$1-').slice(0, 15)) : null;
          const isValid = isComplete && validCoords !== null;
          const hasInput = raw.length > 0;
          // Check if input matches the currently active jump frequency
          const currentJumpFreq = useLocationStore.getState().manualFrequency;
          const inputNorm = raw;
          const jumpNorm = currentJumpFreq?.replace(/-/g, '').toUpperCase() ?? '';
          const isSameAsJump = isManualFreq && inputNorm === jumpNorm;
          const jumpDisabled = !isValid || isSameAsJump;
          const validationColor = !hasInput ? colors.border : (isValid && !isSameAsJump) ? colors.primary : isSameAsJump ? colors.primary : colors.error;
          return (
            <div className="mb-1 shrink-0 rounded-lg px-2 py-1.5" style={{ backgroundColor: colors.surface, border: `1.5px solid ${isManualFreq ? '#FFB300' : colors.border}` }}>
              {/* Frequency input */}
              <div className="relative">
                <input type="text" inputMode="text" autoCapitalize="characters" placeholder="XXX-XXX-XXX-XXX" value={manualFreqInput}
                  onChange={(e) => {
                    let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                    const digits = v.replace(/-/g, '');
                    if (digits.length <= 12) { v = digits.replace(/(.{3})(?=.)/g, '$1-'); }
                    setManualFreqInput(v);
                  }}
                  maxLength={15}
                  className="font-lcd w-full rounded border bg-transparent px-2 py-1.5 pr-7 text-center text-sm tracking-[0.1em] outline-none"
                  style={{ color: colors.lcdText, borderColor: validationColor }} />
                {hasInput && !(isManualFreq && isValid && !isSameAsJump) && (
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold" style={{ color: validationColor }}>
                    {isSameAsJump ? '✓' : isValid ? '✓' : '✗'}
                  </span>
                )}
              </div>
              {/* Buttons */}
              <div className="mt-1.5 flex items-center gap-1.5">
                <button onClick={handleTeleport} disabled={jumpDisabled}
                  className="shrink-0 rounded px-2.5 py-1.5 text-[0.5625rem] font-bold text-white"
                  style={{ backgroundColor: jumpDisabled ? colors.textMuted : colors.primary, opacity: jumpDisabled ? 0.5 : 1 }}>
                  {isKo ? '위치 점프' : 'Jump'}
                </button>
                {isManualFreq && (
                  <button onClick={() => { useLocationStore.getState().cancelTeleport(); setShowManualFreq(false); }}
                    className="shrink-0 rounded px-2 py-1.5 text-[0.5625rem] font-bold"
                    style={{ backgroundColor: `${colors.error}20`, color: colors.error, border: `1px solid ${colors.error}40` }}>
                    {isKo ? '해제' : 'End'}
                  </button>
                )}
                <button onClick={() => setShowManualFreq(false)}
                  className="shrink-0 rounded px-2 py-1.5 text-[0.5625rem] font-bold"
                  style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
                  {isKo ? '닫기' : 'Close'}
                </button>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <p className="text-[0.5rem]" style={{ color: colors.textMuted }}>
                  {isKo ? `점프: ${(config.manualFreqMode.consumptionAmount ?? 100).toLocaleString()}셀 · 발송: ${(config.manualFreqMode.jumpSendCost ?? 50).toLocaleString()}셀/건` : `Jump: ${(config.manualFreqMode.consumptionAmount ?? 100).toLocaleString()} · Send: ${(config.manualFreqMode.jumpSendCost ?? 50).toLocaleString()}/msg`}
                </p>
                {hasInput && !isValid && (
                  <p className="text-[0.5rem] font-bold" style={{ color: colors.error }}>
                    {isKo ? '유효하지 않은 주파수' : 'Invalid frequency'}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {/* === Radar Section === */}
        <div className={`relative mb-1 shrink-0 rounded-xl ${expandedChat ? 'hidden' : ''}`} style={{ backgroundColor: '#0a1a0a', border: `2px solid ${colors.lcdBorder}`, boxShadow: 'inset 0 0 20px rgba(0,0,0,0.6)' }}>
          <div className="flex items-center">
            {/* Left: Sensitivity (tap to change) */}
            <button onClick={() => setShowSensitivityModal(true)}
              className="flex w-20 flex-col items-center justify-center gap-0.5 py-2 hover:bg-white/5"
              style={{ borderRight: `1px solid ${colors.lcdBorder}30` }}>
              <span className="text-[0.5625rem] font-bold uppercase tracking-wider" style={{ color: colors.lcdBorder }}>{t('radio.sensitivity')}</span>
              <span className="font-lcd text-2xl font-bold" style={{ color: colors.lcdNeon }}>{radarRadius}</span>
              <span className="text-[0.625rem] font-bold" style={{ color: colors.lcdBorder }}>km</span>
              <span className="text-[0.5625rem] font-bold" style={{ color: currentRadiusCost > 0 ? colors.warning : colors.primary }}>
                {currentRadiusCost > 0 ? `${currentRadiusCost}${t('battery.cells')}/RX` : t('radio.free')}
              </span>
            </button>

            {/* Center: Radar */}
            <button onClick={() => setShowMap(true)} className="flex flex-1 cursor-pointer items-center justify-center py-1">
              <svg viewBox="0 0 100 100" className="h-[110px] w-[110px]">
                <defs><radialGradient id="rg"><stop offset="0%" stopColor={colors.lcdNeon} stopOpacity="0.08" /><stop offset="100%" stopColor="#000" stopOpacity="0" /></radialGradient></defs>
                <circle cx="50" cy="50" r="46" fill="url(#rg)" stroke={colors.lcdBorder} strokeWidth="1.5" />
                {[34,23,11].map(r=><circle key={r} cx="50" cy="50" r={r} fill="none" stroke={colors.lcdBorder} strokeWidth="0.4" opacity="0.6"/>)}
                <line x1="50" y1="4" x2="50" y2="96" stroke={colors.lcdBorder} strokeWidth="0.3" opacity="0.5"/>
                <line x1="4" y1="50" x2="96" y2="50" stroke={colors.lcdBorder} strokeWidth="0.3" opacity="0.5"/>
                <g className="radar-sweep" style={{ transformOrigin:'50px 50px' }}>
                  <path d="M50,50 L50,6 A44,44 0 0,0 11,27 Z" fill={colors.lcdNeon} opacity="0.06"/>
                  <path d="M50,50 L50,6 A44,44 0 0,0 20,16 Z" fill={colors.lcdNeon} opacity="0.10"/>
                  <path d="M50,50 L50,6 A44,44 0 0,0 33,9 Z" fill={colors.lcdNeon} opacity="0.16"/>
                  <path d="M50,50 L50,6 A44,44 0 0,1 68,9 Z" fill={colors.lcdNeon} opacity="0.22"/>
                  <line x1="50" y1="50" x2="50" y2="6" stroke={colors.lcdNeon} strokeWidth="1.5" opacity="0.8"/>
                </g>
                {nearbyUsers.slice(0,20).map(u=>{
                  const p=getDotPos(u);if(!p)return null;const c=genderColor(u.gender);
                  // Sync dot flash with sweep line (4s cycle, starts at top, clockwise)
                  // Sweep line angle at time t: t/4 * 360 degrees
                  // Dot angle from top (clockwise): atan2(dx, -dy)
                  const dotAngle = Math.atan2(p.cx - 50, -(p.cy - 50));
                  const norm = ((dotAngle + Math.PI * 2) % (Math.PI * 2)) / (Math.PI * 2); // 0-1
                  // Delay = when sweep reaches this dot (negative to shift start)
                  const delay = -(1 - norm) * 4;
                  return<g key={u.uid} className="radar-dot" style={{ animationDelay: `${delay}s` }}>
                    <circle cx={p.cx} cy={p.cy} r="4" fill={c} opacity="0.2"/>
                    <circle cx={p.cx} cy={p.cy} r="2" fill={c}/>
                  </g>;
                })}
                {/* Drops on radar */}
                {effectiveLocation && drops.map(d => {
                  const dist = calcDistance(effectiveLocation.lat, effectiveLocation.lng, d.lat, d.lng);
                  if (dist > radarRadius * 1000) return null;
                  const rm = radarRadius * 1000;
                  const lat1 = effectiveLocation.lat * Math.PI / 180, lat2 = d.lat * Math.PI / 180;
                  const dLng = (d.lng - effectiveLocation.lng) * Math.PI / 180;
                  const bearing = Math.atan2(Math.sin(dLng) * Math.cos(lat2), Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng));
                  // Min offset 6 so drops at same location are visible
                  const rawScale = Math.min(dist / rm, 1) * 42;
                  const scale = dist > 0 ? Math.max(rawScale, 6) : 6;
                  // Fixed angle for 0-distance drops (based on drop id hash)
                  const angle = dist > 0 ? bearing : ((d.id.charCodeAt(0) + d.id.charCodeAt(1)) % 628) / 100;
                  const cx = 50 + scale * Math.sin(angle), cy = 50 - scale * Math.cos(angle);
                  const dropAngle = Math.atan2(cx - 50, -(cy - 50));
                  const dropNorm = ((dropAngle + Math.PI * 2) % (Math.PI * 2)) / (Math.PI * 2);
                  const dropDelay = -(1 - dropNorm) * 4;
                  return <g key={d.id} className="radar-dot" style={{ animationDelay: `${dropDelay}s` }}>
                    <circle cx={cx} cy={cy} r="8" fill="none" stroke="#FFB300" strokeWidth="0.8" opacity="0.2"/>
                    <circle cx={cx} cy={cy} r="5" fill="#FFB300" opacity="0.1"/>
                    <circle cx={cx} cy={cy} r="2" fill="#FFB300" opacity="0.9"/>
                  </g>;
                })}
                <circle cx="50" cy="50" r="5" fill={colors.lcdNeon} opacity="0.12"/><circle cx="50" cy="50" r="2.5" fill={colors.lcdNeon} className="pulse"/>
              </svg>
            </button>

            {/* Right: Nearby / Total tabs */}
            <div className="flex w-24 flex-col self-stretch"
              style={{ borderLeft: `1px solid ${colors.lcdBorder}30` }}>
              {/* Tab buttons */}
              <div className="flex">
                <button onClick={() => setRadarUserTab('nearby')}
                  className="flex-1 py-0.5 text-center text-[0.5rem] font-bold"
                  style={{ color: radarUserTab === 'nearby' ? colors.lcdNeon : colors.lcdBorder, borderBottom: `1.5px solid ${radarUserTab === 'nearby' ? colors.lcdNeon : colors.lcdBorder + '30'}` }}>
                  {isKo ? '주변' : 'Near'}
                </button>
                <button onClick={() => setRadarUserTab('total')}
                  className="flex-1 py-0.5 text-center text-[0.5rem] font-bold"
                  style={{ color: radarUserTab === 'total' ? colors.lcdNeon : colors.lcdBorder, borderBottom: `1.5px solid ${radarUserTab === 'total' ? colors.lcdNeon : colors.lcdBorder + '30'}` }}>
                  {isKo ? '전체' : 'All'}
                </button>
              </div>
              {/* Content - fill remaining space */}
              <div className="flex flex-1 flex-col items-center justify-center">
                {(() => {
                const list = radarUserTab === 'nearby' ? nearbyUsers : totalActiveUsers;
                const gm = list.filter(u=>u.gender==='male');
                const gf = list.filter(u=>u.gender==='female');
                const gu = list.filter(u=>u.gender!=='male'&&u.gender!=='female');
                return (<>
                  <button onClick={() => list.length > 0 && setUserListPopup({ users: list, title: radarUserTab === 'nearby' ? (isKo ? '주변 접속자' : 'Nearby') : (isKo ? '전체 접속자' : 'All Users') })}
                    className="font-lcd text-2xl font-bold leading-none hover:underline" style={{ color: colors.lcdNeon }}>{list.length}</button>
                  <div className="mt-1 flex flex-col items-start gap-0.5 pl-2">
                    <button onClick={() => gm.length > 0 && setUserListPopup({ users: gm, title: isKo?'남성':'Male' })} className="flex items-center gap-1 hover:underline" style={{ color:'#4a9eff' }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor:'#4a9eff' }}/>
                      <span className="text-[0.5625rem] font-bold">{isKo?'남성':'Male'} {gm.length}</span>
                    </button>
                    <button onClick={() => gf.length > 0 && setUserListPopup({ users: gf, title: isKo?'여성':'Female' })} className="flex items-center gap-1 hover:underline" style={{ color:'#ff6b9d' }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor:'#ff6b9d' }}/>
                      <span className="text-[0.5625rem] font-bold">{isKo?'여성':'Female'} {gf.length}</span>
                    </button>
                    <button onClick={() => gu.length > 0 && setUserListPopup({ users: gu, title: isKo?'비공개':'Unknown' })} className="flex items-center gap-1 hover:underline" style={{ color:'#999' }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor:'#999' }}/>
                      <span className="text-[0.5625rem] font-bold">{isKo?'비공개':'Unknown'} {gu.length}</span>
                    </button>
                  </div>
                </>);
              })()}
              </div>
            </div>
          </div>

          {/* Reception mode bar - wraps if needed */}
          <div className="flex flex-wrap items-center gap-1 border-t px-2 py-1" style={{ borderColor: colors.lcdBorder + '30' }}>
            <button
              onClick={() => useLocationStore.getState().setReceptionMode(receptionMode === 'blur_beyond' ? 'sensitivity_only' : 'blur_beyond')}
              className="shrink-0 rounded px-1.5 py-0.5 text-[0.5625rem] font-bold"
              style={{
                backgroundColor: receptionMode === 'blur_beyond' ? `${colors.primary}20` : `${colors.warning}20`,
                color: receptionMode === 'blur_beyond' ? colors.primary : colors.warning,
                border: `1px solid ${receptionMode === 'blur_beyond' ? colors.primary + '60' : colors.warning + '60'}`,
              }}>
              {receptionMode === 'blur_beyond' ? 'BLUR' : 'CUT'}
            </button>
            <span className="shrink-0 text-[0.625rem]" style={{ color: colors.textMuted }}>
              {receptionMode === 'blur_beyond'
                ? `${t('settings.blurBeyond')} (${messageReceptionRadius.toLocaleString()}km)`
                : t('settings.sensitivityOnly')}
            </span>
            {receptionMode === 'blur_beyond' && (
              <div className="flex items-center gap-0.5" style={{ marginLeft: 'auto' }}>
                <span className="mr-1 text-[0.5rem] whitespace-nowrap" style={{ color: colors.textMuted }}>{isKo ? '최대 수신 거리' : 'Max RX'}</span>
                {receptionRangeOptions.map((km) => {
                  const active = messageReceptionRadius === km;
                  return (
                    <button key={km} onClick={() => useLocationStore.getState().setMessageReceptionRadius(km)}
                      className="rounded px-1 py-0.5 text-[0.5625rem] font-bold"
                      style={{
                        backgroundColor: active ? `${colors.lcdNeon}20` : 'transparent',
                        color: active ? colors.lcdNeon : colors.lcdBorder,
                        border: active ? `1px solid ${colors.lcdNeon}40` : '1px solid transparent',
                      }}>
                      {km.toLocaleString()}
                    </button>
                  );
                })}
                <span className="text-[0.5rem] font-bold" style={{ color: colors.lcdBorder }}>km</span>
              </div>
            )}
          </div>
        </div>

        {/* Google Maps overlay */}
        {showMap && (
          <div className={`${mapFullscreen ? 'fixed inset-0 z-40' : 'absolute inset-0 z-40 rounded-2xl'} overflow-hidden`} style={{ backgroundColor: '#0a1a0a' }}>
            <Suspense fallback={<div className="flex h-full items-center justify-center"><p className="font-lcd text-sm" style={{ color: colors.lcdText }}>Loading map...</p></div>}>
              <RadarMap onClose={() => { setShowMap(false); setMapFullscreen(false); setAutoScroll(true); scrollToBottom(); }} fullscreen={mapFullscreen} onToggleFullscreen={() => setMapFullscreen(f => !f)} />
            </Suspense>
          </div>
        )}

        {/* Sensitivity Modal */}
        {showSensitivityModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSensitivityModal(false)}>
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative z-10 w-full max-w-xs rounded-2xl" style={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}` }} onClick={(e) => e.stopPropagation()}>
              <h3 className="px-4 pt-3 pb-0.5 text-center text-base font-bold" style={{ color: colors.text }}>{t('radio.sensitivity')}</h3>
              <p className="mb-2 text-center text-[0.625rem]" style={{ color: colors.textMuted }}>{isKo ? '수신 메시지 1건당 차감되는 배터리' : 'Battery cost per received message'}</p>
              <div className="max-h-72 overflow-y-auto px-3 pb-3">
                {radiusSteps.map((step, idx) => {
                  const active = radarRadius === step.km;
                  // Build cumulative tier description
                  const tierDescs: string[] = [];
                  for (let i = 0; i <= idx; i++) {
                    const s = radiusSteps[i];
                    const prevKm = i === 0 ? 0 : radiusSteps[i - 1].km;
                    if (s.cost === 0) {
                      tierDescs.push(isKo ? `0~${s.km}km : ${t('radio.free')}` : `0~${s.km}km: Free`);
                    } else {
                      tierDescs.push(isKo ? `~${s.km}km : ${s.cost}${t('battery.cells')}` : `~${s.km}km: ${s.cost} cells`);
                    }
                  }
                  return (
                    <button key={step.km}
                      onClick={() => { useLocationStore.getState().setRadarRadius(step.km); setShowSensitivityModal(false); }}
                      className="mb-1.5 flex w-full items-center rounded-lg px-4 py-3 transition-colors"
                      style={{ backgroundColor: active ? `${colors.primary}20` : colors.card, border: `1.5px solid ${active ? colors.primary : 'transparent'}` }}>
                      <div className="flex-1 text-left">
                        <span className="text-base font-bold" style={{ color: active ? colors.lcdNeon : colors.text }}>{step.km}km</span>
                        <div className="mt-0.5">
                          {tierDescs.map((desc, di) => (
                            <p key={di} className="text-[0.5625rem]" style={{ color: colors.textMuted }}>{desc}</p>
                          ))}
                        </div>
                      </div>
                      {active && <span className="ml-2 text-lg" style={{ color: colors.primary }}>✓</span>}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 border-t px-3 pb-3 pt-3" style={{ borderColor: colors.border }}>
                <button onClick={() => setShowSensitivityModal(false)} className="w-full rounded-lg py-2.5 text-sm font-bold" style={{ backgroundColor: colors.surface, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>{t('common.close')}</button>
              </div>
            </div>
          </div>
        )}

        {/* LCD Message Area */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg" style={{ backgroundColor: themeConfig.chatBg, border: `2px solid ${colors.lcdBorder}`, boxShadow: 'inset 0 0 15px rgba(0,0,0,0.4)' }}>
          <div className="flex-1 overflow-y-auto px-2 py-2" ref={messagesContainerRef}
            onScroll={(e) => {
              if (!initialLoadDone.current || programmaticScrollRef.current) return;
              const el = e.currentTarget;
              // Use larger threshold (100px) to prevent keyboard resize from accidentally exiting auto-scroll
              const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
              setAutoScroll(atBottom);
              if (atBottom) setUnreadCount(0);
              // Date overlay
              const msgs = el.querySelectorAll('[data-ts]');
              for (const m of msgs) {
                const rect = (m as HTMLElement).getBoundingClientRect();
                const containerRect = el.getBoundingClientRect();
                if (rect.top >= containerRect.top) {
                  const ts = Number((m as HTMLElement).dataset.ts);
                  if (ts) { setScrollDateLabel(fmtDate(ts)); if (scrollDateTimeout.current) clearTimeout(scrollDateTimeout.current); scrollDateTimeout.current = setTimeout(() => setScrollDateLabel(''), 2000); }
                  break;
                }
              }
            }}>
            {sorted.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 opacity-40">
                <IconRadio size={32} color={colors.lcdBorder} />
                <p className="font-lcd text-xs" style={{ color: colors.lcdBorder }}>{t('radio.messagePlaceholder')}</p>
              </div>
            ) : sorted.map((msg, idx) => {
              const showDate = idx === 0 || !sameDay(msg.timestamp, sorted[idx-1].timestamp);
              const msgGc = genderColor(msg.gender);
              // Use latest profile from allUsersRaw if available
              const latestUser = !msg.isMine ? allUsersRaw.find(u => u.uid === msg.senderId) : undefined;
              const avatarUrl = msg.isMine ? user?.profileImageUrl : (latestUser?.profileImageUrl ?? msg.profileImageUrl);
              const avatarGender = msg.isMine ? (user?.gender ?? 'unknown') : (latestUser?.gender ?? msg.gender);
              const avatarGc = genderColor(avatarGender);
              return (
                <div key={msg.id} data-ts={msg.timestamp} data-msg-id={msg.id}>
                  {showDate && (
                    <div className="my-3 flex items-center gap-2">
                      <div className="flex-1 border-t" style={{ borderColor: colors.lcdBorder+'50' }}/>
                      <span className="font-lcd text-[0.625rem]" style={{ color: colors.lcdBorder }}>{fmtDate(msg.timestamp)}</span>
                      <div className="flex-1 border-t" style={{ borderColor: colors.lcdBorder+'50' }}/>
                    </div>
                  )}
                  {msg.isNearbyNotification ? (
                  /* Nearby notification - avatar + special badge style */
                  <div className={`mb-2 flex gap-1.5 ${msg.isMine ? 'justify-end' : ''}`}>
                    <div className="relative mt-0.5 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); setContextMenu({ msg, x: e.clientX, y: e.clientY }); }}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-full"
                        style={{ border: `2px solid ${avatarGc}`, backgroundColor: '#1a2a1a' }}>
                        {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover"/> : <DefaultAvatar gender={avatarGender} size={20} />}
                      </button>
                      {msg.isBot && config.showBotBadge !== false && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[6px] font-bold text-white"
                          style={{ backgroundColor: '#9333ea', border: '1.5px solid #1a2a1a' }}>B</span>
                      )}
                    </div>
                    <div className="max-w-[80%]">
                      <div className="inline-block rounded-lg px-3 py-1.5" style={{ backgroundColor: 'rgba(255,179,0,0.08)', border: `1.5px dashed rgba(255,179,0,0.4)` }}>
                        <p className="mb-0.5 flex items-center gap-1 text-xs font-bold" style={{ color: '#FFB300' }}>
                          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: msgGc }} />
                          {msg.senderNickname}
                          {msg.isBot && config.showBotBadge !== false && <span className="rounded bg-purple-600/30 px-1 text-[0.5625rem] text-purple-400">BOT</span>}
                          {msg.distance != null && <span className="font-normal text-[0.625rem]" style={{ color: '#aa8833' }}>{fmtDist(msg.distance)}</span>}
                        </p>
                        <p className="text-sm font-bold" style={{ color: '#FFB300' }}>{msg.text}</p>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <span className="font-lcd text-[0.625rem] tracking-wider" style={{ color: '#aa8833' }}>{msg.frequency}</span>
                          <span className="text-[0.6875rem]" style={{ color: '#aa8833' }}>{fmtTime(msg.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  ) : (
                  <div className={`mb-2 flex gap-1.5 ${msg.isMine ? 'justify-end' : ''}`}>
                    <div className="relative mt-0.5 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); setContextMenu({ msg, x: e.clientX, y: e.clientY }); }}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-full"
                        style={{ border: `2px solid ${avatarGc}`, backgroundColor: '#1a2a1a' }}>
                        {avatarUrl
                          ? <img src={avatarUrl} alt="" className="h-full w-full object-cover"/>
                          : <DefaultAvatar gender={avatarGender} size={20} />}
                      </button>
                      {msg.isBot && config.showBotBadge !== false && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[6px] font-bold text-white"
                          style={{ backgroundColor: '#9333ea', border: '1.5px solid #1a2a1a' }}>B</span>
                      )}
                    </div>
                    <div style={{ maxWidth: '75%' }}>
                      <div className="inline-block rounded-2xl px-2.5 py-1.5"
                        style={{
                          backgroundColor: msg.isMine ? themeConfig.myBubbleBg : themeConfig.otherBubbleBg,
                          border: `1.5px solid ${msg.isMine ? themeConfig.myBubbleBorder : themeConfig.otherBubbleBorder}`,
                          borderTopRightRadius: msg.isMine ? 4 : undefined,
                          borderTopLeftRadius: !msg.isMine ? 4 : undefined,
                        }}
>
                        <p className="mb-0.5 flex items-center gap-1 text-xs font-bold" style={{ color: msg.isMine ? (themeConfig.myTextColor + 'bb') : '#779977' }}>
                          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: msg.isMine ? genderColor(user?.gender ?? 'unknown') : msgGc }} />
                          {msg.isMine ? (user?.nickname ?? '') : msg.senderNickname}
                          {msg.isBot && config.showBotBadge !== false && <span className="rounded bg-purple-600/30 px-1 text-[0.5625rem] text-purple-400">BOT</span>}
                          {!msg.isMine && msg.distance != null && <span className="font-normal" style={{ color: '#556655' }}>{fmtDist(msg.distance)}</span>}
                        </p>
                        <p className="font-lcd text-sm" style={{
                          color: msg.isBlurred ? '#5a7a5a' : (msg.isMine ? themeConfig.myTextColor : themeConfig.otherTextColor),
                          filter: msg.isBlurred ? 'blur(3px)' : 'none',
                          userSelect: msg.isBlurred ? 'none' : 'auto',
                          opacity: msg.isBlurred ? 0.7 : 1,
                        }}>{msg.text}</p>
                        <div className="mt-0.5 flex items-center gap-1.5" style={{ justifyContent: msg.isMine ? 'flex-end' : 'flex-start' }}>
                          <span className="font-lcd text-[0.625rem] tracking-wider" style={{ color: msg.isMine ? (themeConfig.myTextColor + '99') : '#667766' }}>{msg.frequency}</span>
                          <span className="text-[0.6875rem]" style={{ color: msg.isMine ? (themeConfig.myTextColor + '88') : '#556655' }}>{fmtTime(msg.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              );
            })}
            {gapFilling && (
              <div className="flex items-center justify-center py-2">
                <span className="animate-pulse text-[10px]" style={{ color: colors.textMuted }}>
                  {isKo ? '부재 중 메시지 불러오는 중...' : 'Loading missed messages...'}
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Date overlay - semi-transparent, with padding from top */}
          {scrollDateLabel && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full px-4 py-1"
              style={{ backgroundColor: 'rgba(20,40,20,0.55)' }}>
              <span className="font-lcd text-[0.625rem] font-bold" style={{ color: colors.lcdText }}>{scrollDateLabel}</span>
            </div>
          )}

          {/* Expand/collapse chat button - top right */}
          <button onClick={() => setExpandedChat(!expandedChat)}
            className="absolute top-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded"
            style={{ backgroundColor: 'rgba(20,40,20,0.5)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.lcdNeon} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
              {expandedChat
                ? <><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></>
                : <><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>
              }
            </svg>
          </button>

          {/* Auto-scroll button - semi-transparent circle */}
          {!autoScroll && (
            <button onClick={() => { setAutoScroll(true); setUnreadCount(0); scrollToBottom(); }}
              className="absolute bottom-14 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full shadow-lg"
              style={{ backgroundColor: 'rgba(20,40,20,0.5)', border: `1px solid ${colors.lcdNeon}25` }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.lcdNeon} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[0.5rem] font-bold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          )}

          {/* Input */}
          <form onSubmit={handleSend} className="flex items-center gap-2 px-2 py-1.5"
            onTouchMove={(e) => e.stopPropagation()}
            style={{ borderTop: `1.5px solid ${user?.chatLocked ? colors.error : colors.lcdBorder}`, touchAction: 'none' }}>
            <div className="flex flex-1 overflow-hidden rounded-lg" style={{ backgroundColor: user?.chatLocked ? `${colors.error}10` : themeConfig.inputBg, border: `1.5px solid ${user?.chatLocked ? colors.error : themeConfig.inputBorder}` }}>
              <input type="text" value={inputText} onChange={(e) => setInputText(e.target.value)}
                disabled={!!user?.chatLocked}
                placeholder={user?.chatLocked
                  ? (isKo ? '관리자에 의해 채팅이 잠겼습니다' : 'Chat has been locked by admin')
                  : `${t('radio.messagePlaceholder')} (${(() => { const cost = isManualFreq ? (config.manualFreqMode.jumpSendCost ?? 50) : config.messageSendCost; return cost > 0 ? cost + t('battery.cells') : t('radio.free'); })()})`}
                className={`font-lcd w-full bg-transparent px-3 py-2 outline-none${user?.chatLocked ? ' chat-locked-input' : ''}`}
                style={{ color: user?.chatLocked ? colors.error : colors.lcdText, fontSize: 'clamp(0.65rem, 2.5vw, 0.875rem)' }} />
            </div>
            <button type="submit" disabled={!!user?.chatLocked} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg disabled:opacity-30"
              style={{ backgroundColor: '#0d1a0d', border: `1.5px solid ${user?.chatLocked ? colors.error : colors.lcdBorder}`, boxShadow: inputText.trim() && !user?.chatLocked ? `0 0 8px ${colors.lcdNeon}40` : 'none' }}>
              <IconSend size={16} color={user?.chatLocked ? colors.error : (inputText.trim() ? colors.lcdNeon : colors.lcdBorder)} />
            </button>
          </form>
        </div>


        {/* Context Menu */}
        {contextMenu && (() => {
          const isMineMenu = contextMenu.msg.isMine;
          const ic = (sym: string, col: string) => <span className="flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold" style={{ backgroundColor: col + '20', color: col }}>{sym}</span>;
          const menuItems = isMineMenu
            ? [
                { label: t('profile.title'), icon: ic('P', colors.primary), action: 'profile' },
                { label: t('profile.copyFrequency'), icon: ic('F', '#8bb8e8'), action: 'copyFreq' },
                { label: t('common.copyMessage'), icon: ic('C', '#b39ddb'), action: 'copyText' },
              ]
            : [
                { label: t('profile.title'), icon: ic('P', colors.primary), action: 'profile' },
                { label: t('profile.sendDM'), icon: ic('M', colors.info), action: 'dm' },
                { label: t('radio.manualFreq'), icon: ic('J', '#FFB300'), action: 'jump', color: '#FFB300' },
                { label: t('profile.copyFrequency'), icon: ic('F', '#8bb8e8'), action: 'copyFreq' },
                { label: t('common.copyMessage'), icon: ic('C', '#b39ddb'), action: 'copyText' },
                { label: t('profile.report'), icon: ic('!', colors.warning), action: 'report' },
                { label: t('profile.block'), icon: ic('X', colors.error), action: 'block', color: colors.error },
              ];
          return (
            <div id="ctx-menu" ref={(el) => {
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const vw = window.innerWidth;
                const vh = window.visualViewport?.height ?? window.innerHeight;
                let x = contextMenu.x;
                let y = contextMenu.y;
                if (x + rect.width > vw - 8) x = vw - rect.width - 8;
                if (x < 8) x = 8;
                if (y + rect.height > vh - 8) y = y - rect.height - 8;
                if (y < 8) y = 8;
                el.style.left = `${x}px`;
                el.style.top = `${y}px`;
              }}
              className="fixed z-50 min-w-[180px] overflow-hidden rounded-lg shadow-2xl"
              style={{ left: -9999, top: -9999, backgroundColor: colors.card, border: `1.5px solid ${colors.border}` }}
              onMouseDown={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
              <div className="border-b px-3 py-2" style={{ borderColor: colors.border }}>
                <p className="text-xs font-bold" style={{ color: colors.text }}>{contextMenu.msg.senderNickname}</p>
                <p className="font-lcd text-[0.5625rem] tracking-wider" style={{ color: colors.lcdNeon }}>{contextMenu.msg.frequency}</p>
                {contextMenu.msg.distance != null && <p className="text-[0.625rem]" style={{ color: colors.textMuted }}>{fmtDist(contextMenu.msg.distance)}</p>}
              </div>
              {menuItems.map((item) => (
                <button key={item.action}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5"
                  style={{ color: (item as any).color ?? colors.text }}
                  onClick={() => {
                    const cm = contextMenu; setContextMenu(null);
                    if (item.action === 'profile') window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: cm.msg.senderId, nickname: cm.msg.senderNickname } }));
                    else if (item.action === 'dm') window.dispatchEvent(new CustomEvent('open-dm-compose', { detail: { recipientId: cm.msg.senderId, recipientNickname: cm.msg.senderNickname } }));
                    else if (item.action === 'jump') { confirmAndJump(cm.msg.frequency); }
                    else if (item.action === 'copyFreq') { navigator.clipboard.writeText(cm.msg.frequency); }
                    else if (item.action === 'copyText') navigator.clipboard.writeText(cm.msg.text);
                    else if (item.action === 'report') window.dispatchEvent(new CustomEvent('open-report', { detail: { targetId: cm.msg.senderId, targetNickname: cm.msg.senderNickname, contentType: 'message', contentPreview: cm.msg.text, contentTimestamp: cm.msg.timestamp } }));
                    else if (item.action === 'block') {
                      if (user && confirm(`${cm.msg.senderNickname}님을 차단하시겠습니까?`)) {
                        import('../../services/userService').then(({ blockUser }) => {
                          blockUser(user.uid, cm.msg.senderId);
                          useAuthStore.getState().setUser({ ...user, blockedUserIds: [...(user.blockedUserIds ?? []), cm.msg.senderId] });
                        });
                      }
                    }
                  }}>
                  {item.icon}{item.label}
                </button>
              ))}
            </div>
          );
        })()}

        {/* User list popup */}
        {userListPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setUserListPopup(null)}>
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl" style={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}` }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <h3 className="text-sm font-bold" style={{ color: colors.text }}>{userListPopup.title} ({userListPopup.users.length})</h3>
                <button onClick={() => setUserListPopup(null)} className="text-lg" style={{ color: colors.textMuted }}>✕</button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {userListPopup.users.map((u) => {
                  const gc = u.gender === 'male' ? '#4a9eff' : u.gender === 'female' ? '#ff6b9d' : '#999';
                  const isMe = u.uid === user?.uid;
                  return (
                    <div key={u.uid} className="flex items-center gap-2 px-4 py-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <div className="relative">
                        <button onClick={() => { window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: u.uid, nickname: u.nickname } })); }}
                          className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full" style={{ border: `2px solid ${gc}`, backgroundColor: colors.card }}>
                          {u.profileImageUrl ? <img src={u.profileImageUrl} alt="" className="h-full w-full object-cover"/> : <DefaultAvatar gender={u.gender} size={20}/>}
                        </button>
                        {u.isBot && config.showBotBadge !== false && <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[6px] font-bold text-white" style={{ backgroundColor: '#9333ea', border: `1.5px solid ${colors.card}` }}>B</span>}
                      </div>
                      <button onClick={() => { window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: u.uid, nickname: u.nickname } })); }}
                        className="flex-1 text-left">
                        <span className="text-sm font-bold" style={{ color: colors.text }}>{u.nickname} {isMe ? (isKo ? '(나)' : '(Me)') : ''}</span>
                        {u.isBot && config.showBotBadge !== false && <span className="ml-1 rounded bg-purple-600/30 px-1 text-[8px] font-bold text-purple-400">BOT</span>}
                        {u.distance != null && u.distance > 0 && <span className="ml-1 text-xs" style={{ color: colors.textMuted }}>{u.distance < 1000 ? `${Math.round(u.distance)}m` : `${(u.distance/1000).toFixed(1)}km`}</span>}
                      </button>
                      {!isMe && (
                        <button onClick={() => { setUserListPopup(null); window.dispatchEvent(new CustomEvent('open-dm-compose', { detail: { recipientId: u.uid, recipientNickname: u.nickname } })); }}
                          className="shrink-0 rounded px-2 py-1 text-[0.625rem] font-bold text-white" style={{ backgroundColor: colors.primary }}>DM</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
